import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, handleConnectionState, retryOperation } from './firebase';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut, createUserWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { collection, doc, getDoc, onSnapshot, addDoc, setDoc, deleteDoc, serverTimestamp, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { getStorage, ref, uploadString, getDownloadURL } from 'firebase/storage';
import { FeeSettings } from './components/FeeSettings';
import ParentPortal from './ParentPortal';
import AdminDashboard from './components/AdminDashboard';
import HomeworkManagement from './components/HomeworkManagement';
import NotificationManagement from './components/NotificationManagement';

// Define global app ID
const __app_id = 'school-management-system-app';
const APP_ID = __app_id;

// Network connection state component
const NetworkDetector = () => {
    useEffect(() => {
        // Function to handle online status
        const handleOnline = () => {
            console.log('Application is online');
            handleConnectionState(true);
        };

        // Function to handle offline status
        const handleOffline = () => {
            console.log('Application is offline');
            handleConnectionState(false);
        };

        // Initial connection check
        if (navigator.onLine) {
            handleConnectionState(true);
        } else {
            handleConnectionState(false);
        }

        // Add event listeners
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Set up periodic connection check
        const connectionCheckInterval = setInterval(() => {
            // Create a timestamp to prevent caching
            const timestamp = new Date().getTime();

            // Try to fetch a small resource to test connection
            fetch(`/favicon.ico?t=${timestamp}`, { method: 'HEAD', cache: 'no-store' })
                .then(() => {
                    if (!navigator.onLine) {
                        // Browser thinks we're offline but we just made a successful request
                        // Force the online event
                        window.dispatchEvent(new Event('online'));
                    }
                })
                .catch(() => {
                    if (navigator.onLine) {
                        // Browser thinks we're online but request failed
                        // Force the offline event
                        window.dispatchEvent(new Event('offline'));
                    }
                });
        }, 30000); // Check every 30 seconds

        // Clean up
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(connectionCheckInterval);
        };
    }, []);

    return null; // This component doesn't render anything
};


// --- !!! إعدادات مهمة للنشر (Deployment) !!! ---
// تم نقل إعدادات Firebase إلى ملف firebase.js
// استخدام المثيلات المستوردة من firebase.js

// Locale mapping used across top-level components (components are declared before App)
const LOCALE_MAP = {
    ar: 'ar-TN',
    fr: 'fr-FR',
    en: 'en-GB'
};
// mutable module-level locale used by components defined above `App`
let APP_LOCALE = LOCALE_MAP['ar'];

// simple translation getter: keyPath like 'pageTitles.home' -> returns translated string or fallback
function t(keyPath, fallback) {
    try {
        const lang = (typeof window !== 'undefined' && window.__school_settings && window.__school_settings.language) ? window.__school_settings.language : 'ar';
        let node = TRANSLATIONS[lang];
        if (!node) node = TRANSLATIONS['ar'];
        const parts = keyPath.split('.');
        for (const p of parts) {
            if (!node[p]) return fallback || keyPath;
            node = node[p];
        }
        return node;
    } catch (err) {
        return fallback || keyPath;
    }
}

function isRTL(lang) {
    return lang === 'ar';
}

// Simple translations map for core UI strings (page titles + a few labels).
// Add more keys as needed. This is intentionally small to avoid massive edits.
const TRANSLATIONS = {
    ar: {
        pageTitles: {
            home: 'لوحة المعلومات الرئيسية',
            studentManagement: 'إدارة التلامذة',
            parentManagement: 'إدارة أولياء الأمور',
            teacherManagement: 'إدارة المعلمين',
            classManagement: 'إدارة الأقسام',
            subjectManagement: 'إدارة المواد',
            finance: 'الإدارة المالية',
            timetable: 'الجدول الدراسي',
            settings: 'الإعدادات',
            homeworkManagement: 'إدارة الواجبات المدرسية',
        },
        labels: {
            academicYear: 'العام الدراسي',
            manageStudentsDesc: 'إدارة بيانات التلامذة وتسجيلهم.',
            manageTeachersDesc: 'ملفات، جداول، وتقييمات.',
            save: 'حفظ',
            update: 'تحديث',
            saveChanges: 'حفظ التغييرات',
            add: 'إضافة',
            addStudent: 'إضافة تلميذ',
            addUser: 'إضافة مستخدم',
            logout: 'تسجيل الخروج',
            settingsTitle: 'إعدادات النظام',
            manageUsersTitle: 'إدارة المستخدمين والصلاحيات',
            confirmDeleteStudent: 'هل أنت متأكد أنك تريد حذف هذا التلميذ؟ لا يمكن التراجع عن العملية.'
        }
    },
    fr: {
        pageTitles: {
            home: 'Tableau de bord',
            studentManagement: 'Gestion des élèves',
            parentManagement: 'Gestion des parents',
            teacherManagement: 'Gestion des enseignants',
            classManagement: 'Gestion des classes',
            subjectManagement: 'Gestion des matières',
            finance: 'Finances',
            timetable: 'Emploi du temps',
            settings: 'Paramètres',
        },
        labels: {
            academicYear: 'Année scolaire',
            manageStudentsDesc: 'Gérer les dossiers des élèves et les inscriptions.',
            manageTeachersDesc: 'Profils, emplois du temps et évaluations.',
            save: 'Enregistrer',
            update: 'Mettre à jour',
            saveChanges: 'Enregistrer les modifications',
            add: 'Ajouter',
            addStudent: 'Ajouter un élève',
            addUser: 'Ajouter un utilisateur',
            logout: 'Se déconnecter',
            settingsTitle: 'Paramètres du système',
            manageUsersTitle: 'Gestion des utilisateurs et des rôles',
            confirmDeleteStudent: 'Êtes-vous sûr de vouloir supprimer cet élève ? Cette action est irréversible.'
        }
    },
    en: {
        pageTitles: {
            home: 'Dashboard',
            studentManagement: 'Student Management',
            parentManagement: 'Parent Management',
            teacherManagement: 'Teacher Management',
            classManagement: 'Class Management',
            subjectManagement: 'Subject Management',
            finance: 'Finance',
            timetable: 'Timetable',
            settings: 'Settings',
        },
        labels: {
            academicYear: 'Academic Year',
            manageStudentsDesc: 'Manage student records and enrollment.',
            manageTeachersDesc: 'Profiles, schedules, and evaluations.',
            save: 'Save',
            update: 'Update',
            saveChanges: 'Save changes',
            add: 'Add',
            addStudent: 'Add student',
            addUser: 'Add user',
            logout: 'Logout',
            settingsTitle: 'System Settings',
            manageUsersTitle: 'User Management & Roles',
            confirmDeleteStudent: 'Are you sure you want to delete this student? This cannot be undone.'
        }
    }
};

const LANGUAGE_FLAGS = { ar: '🇹🇳', fr: '🇫🇷', en: '🇬🇧' };

// --- نهاية الإعدادات المهمة ---



// --- Component for Global Styles & Fonts ---
// مكون نافذة تأكيد احترافية
const ConfirmModal = ({ show, message, onConfirm, onCancel }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[200] flex items-center justify-center">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md text-center">
                <h3 className="text-xl font-bold mb-4 text-red-700">تحذير</h3>
                <p className="mb-6 text-gray-700">{message}</p>
                <div className="flex justify-center gap-4">
                    <button onClick={onCancel} className="bg-gray-400 text-white px-6 py-2 rounded-lg font-bold hover:bg-gray-500">إلغاء</button>
                    <button onClick={onConfirm} className="bg-red-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-red-700">تأكيد</button>
                </div>
            </div>
        </div>
    );
};
const GlobalStyles = () => (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
        
        body {
            font-family: 'Cairo', sans-serif;
            background-color: #f8f9fa;
        }

        .header-gradient {
            background: linear-gradient(to right, #ffffff, #f2f6ff);
        }

        .card {
            transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
        }

        .card:hover {
            transform: translateY(-5px);
            box-shadow: 0 10px 20px rgba(0,0,0,0.08);
        }
        
        @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes fadeInUp {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideInUp {
            from { transform: translateY(100%); opacity: 0; }
            to { transform: translateY(0); opacity: 1; }
        }

        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }

        .modal-content {
            animation: fadeIn 0.3s ease-out forwards;
        }
        
        .notification-toast {
            animation: slideInUp 0.5s ease-out forwards;
        }

        .fade-in-up {
            animation: fadeInUp 0.6s ease-out forwards;
        }

        .slide-in-right {
            animation: slideInRight 0.3s ease-out forwards;
        }

        @keyframes growPie {
            from { transform: scale(0.9); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
        }

        .pie-segment {
            transform-origin: 50% 50%;
            animation: growPie 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
    `}</style>
);


// --- SVG Icons ---
const UserPlusIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 11a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1v-1z" />
    </svg>
);
const LibraryIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M2 20h20v2H2v-2zm2-18v16h16V2H4zm14 14H6V4h12v12zM8 6h8v2H8V6zm0 4h8v2H8v-2z" />
    </svg>
);
const ClipboardListIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor" {...props}>
        <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
        <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
    </svg>
);
const TrashIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.134-2.09-2.134H8.09c-1.18 0-2.09.954-2.09 2.134v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
    </svg>
);
const PrintIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V6.375c0-.621-.504-1.125-1.125-1.125H8.625c-.621 0-1.125.504-1.125 1.125v2.955m10.5 0h-10.5" />
    </svg>
);
const Spinner = () => (
    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);
const LogoutIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
    </svg>
);
const UsersGroupIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m-7.5-2.962a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18 18.72a9.094 9.094 0 01-7.5 0" />
    </svg>
);
const DollarSignIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 11.21 12.75 11 12 11c-.75 0-1.536.21-2.121.562m-2.628 2.628a2.25 2.25 0 003.182 0l.879-.659m0 0c-1.171-.879-3.07-.879-4.242 0-1.172.879-1.172 2.303 0 3.182s2.303 1.172 3.182 0z" />
    </svg>
);
const CalendarIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0h18M-4.5 12h22.5" />
    </svg>
);
const EyeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.432 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);
const ClipboardDocumentCheckIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.125 2.25h-4.5c-1.125 0-2.25.9-2.25 2.25v13.5c0 1.125.9 2.25 2.25 2.25h9c1.125 0 2.25-.9 2.25-2.25v-9.75M14.25 9l-3.75 3.75-1.5-1.5M12 9h.008v.008H12V9z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 9.75L16.5 12l3.75-3.75" />
    </svg>
);
const ExclamationTriangleIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
);
const PencilIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
);
const SearchIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
);
const HomeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
    </svg>
);
const AcademicCapIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.438 60.438 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0l15.482 0m-15.482 0a2.25 2.25 0 01-2.24-2.029l-.35-1.75a2.25 2.25 0 01.9-2.458l.7-1.05a2.25 2.25 0 013.25-.337L12 10.5l5.52-3.218a2.25 2.25 0 013.25.337l.7 1.05a2.25 2.25 0 01.9 2.458l-.35 1.75a2.25 2.25 0 01-2.24 2.029m-15.482 0l15.482 0" />
    </svg>
);
const BellIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
    </svg>
);
const Cog6ToothIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.438.995a6.473 6.473 0 010 1.91l.438.995c.06.494.223.99.438 1.442l1.003.827c.432.355.622.9.26 1.431l-1.296 2.247a1.125 1.125 0 01-1.37.49l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.332.183-.582.495-.645.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.063-.374-.313-.686-.645-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.37-.49l-1.296-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.437-.995a6.473 6.473 0 010-1.91l-.437-.995a.983.983 0 01-.437-1.442l-1.004-.827a1.125 1.125 0 01-.26-1.431l1.296-2.247a1.125 1.125 0 011.37-.49l1.217.456c.355.133.75.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.645-.869l.213-1.28z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);
const ChartPieIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6a7.5 7.5 0 100 12h-3a7.5 7.5 0 000-12h3z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6a7.5 7.5 0 017.5 7.5h-3a4.5 4.5 0 00-4.5-4.5v-3z" />
    </svg>
);
const ArrowUpOnSquareIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25H15M9 12l3 3m0 0l3-3m-3 3V2.25" />
    </svg>
);
const XMarkIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);
const EnvelopeIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
    </svg>
);
const BriefcaseIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.075a2.25 2.25 0 01-2.25 2.25h-12a2.25 2.25 0 01-2.25-2.25V14.15M16.5 18.75h-9M9.75 14.15V3.75a2.25 2.25 0 012.25-2.25h.008a2.25 2.25 0 012.25 2.25v10.4" />
    </svg>
);
const StarIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
);
const ClockIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);
const ChevronDownIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
);
const ShieldCheckIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.286zm0 13.036h.008v.008h-.008v-.008z" />
    </svg>
);
const PaintBrushIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.43 2.43h10.26a2.25 2.25 0 012.43-2.43c0-.464-.184-.905-.488-1.234a3 3 0 00-5.78-1.128 2.25 2.25 0 01-2.43-2.43zM3.75 6.375c0-.621.504-1.125 1.125-1.125h14.25c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125H4.875A1.125 1.125 0 013.75 7.875v-1.5z" />
    </svg>
);
const BuildingOffice2Icon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6M9 11.25h6m-6 4.5h6M4.5 21V3m0 18h-1.5" />
    </svg>
);
const KeyIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
    </svg>
);
const BookOpenIcon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
    </svg>
);
const Bars3Icon = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
    </svg>
);

// --- Sub-components (Forms, Modals, etc.) ---
const Notification = ({ message, type, onClear }) => {
    useEffect(() => {
        // ...existing code...
        if (!message) return;
        const timer = setTimeout(() => {
            onClear();
        }, 4000);
        return () => clearTimeout(timer);
    }, [message, onClear]);

    const colors = {
        success: 'bg-green-500',
        error: 'bg-red-500',
        info: 'bg-blue-500',
    };

    if (!message) return null;

    return (
        <div className={`fixed top-20 right-5 z-[100] p-4 rounded-lg text-white shadow-lg notification-toast ${colors[type] || 'bg-gray-800'}`}>
            {message}
        </div>
    );
};

const Modal = ({ show, onClose, title, children, maxWidth = 'max-w-lg' }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={onClose}>
            <div className={`bg-white rounded-xl shadow-2xl w-full ${maxWidth} modal-content`} onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center border-b p-4">
                    <h3 className="text-xl font-bold text-gray-800">{title}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-3xl leading-none">&times;</button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
};

const AddStudentForm = ({ onSave, onDone, initialData, showNotification, parents }) => {
    const [formData, setFormData] = useState({
        firstName: '', lastName: '', birthDate: '', fatherName: '', motherName: '',
        fatherPhone: '', motherPhone: '', email: '', photoUrl: '', parentPhone: '', studentId: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedParentId, setSelectedParentId] = useState('');

    useEffect(() => {
        if (initialData) {
            setFormData({
                ...initialData,
                firstName: initialData.firstName || '',
                lastName: initialData.lastName || '',
                birthDate: initialData.birthDate || '',
                fatherName: initialData.fatherName || '',
                motherName: initialData.motherName || '',
                fatherPhone: initialData.fatherPhone || '',
                motherPhone: initialData.motherPhone || '',
                email: initialData.email || '',
                photoUrl: initialData.photoUrl || '',
                parentPhone: initialData.parentPhone || '',
                studentId: initialData.studentId || ''
            });
            if (initialData.parentPhone) {
                setSelectedParentId(initialData.parentPhone);
            } else {
                setSelectedParentId('');
            }
        } else {
            setFormData({
                firstName: '', lastName: '', birthDate: '', fatherName: '', motherName: '',
                fatherPhone: '', motherPhone: '', email: '', photoUrl: '', parentPhone: '', studentId: ''
            });
            setSelectedParentId('');
        }
    }, [initialData]);

    const handleParentSelect = (e) => {
        const parentId = e.target.value;
        setSelectedParentId(parentId);
        if (parentId === '') { // "Add New Parent" selected
            setFormData(prev => ({
                ...prev,
                fatherName: '', motherName: '', fatherPhone: '', motherPhone: '', email: '', parentPhone: ''
            }));
        } else {
            const selectedParent = parents.find(p => p.id === parentId);
            if (selectedParent) {
                setFormData(prev => ({
                    ...prev,
                    fatherName: selectedParent.name,
                    fatherPhone: selectedParent.phone,
                    email: selectedParent.email,
                    parentPhone: selectedParent.phone, // Ensure parentPhone is set for existing parent
                    motherName: '', // Clear mother's name if selecting existing father
                    motherPhone: '', // Clear mother's phone if selecting existing father
                }));
            }
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, photoUrl: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };


    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.firstName || !formData.lastName || !formData.birthDate) {
            showNotification('الرجاء ملء حقول التلميذ الأساسية.', 'error');
            return;
        }
        // Validate birthDate is not in the future
        try {
            const birth = new Date(formData.birthDate);
            const today = new Date();
            // normalize to date-only for comparison
            birth.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            if (birth > today) {
                showNotification('تاريخ الميلاد لا يمكن أن يكون في المستقبل.', 'error');
                return;
            }
        } catch (err) {
            // if parsing failed, fall through and let backend/other validation handle it
        }
        setIsSubmitting(true);
        await onSave(formData);
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
            <h4 className="font-bold text-gray-600 border-b pb-2">صورة التلميذ</h4>
            <div className="flex items-center gap-4">
                <img
                    src={formData.photoUrl || 'https://placehold.co/100x100/E2E8F0/4A5568?text=صورة'}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                />
                <label className="block">
                    <span className="sr-only">اختر صورة الملف الشخصي</span>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-full file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100
                    "/>
                </label>
            </div>

            <h4 className="font-bold text-gray-600 border-b pb-2 pt-4">بيانات التلميذ</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="firstName" value={formData.firstName} onChange={handleChange} placeholder="الاسم الأول" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" required />
                <input name="lastName" value={formData.lastName} onChange={handleChange} placeholder="اللقب" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" required />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="studentId" value={formData.studentId} onChange={handleChange} placeholder="الرقم الدراسي (يدوي)" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" />
                <input name="birthDate" type="date" value={formData.birthDate} onChange={handleChange} className="p-3 border rounded-lg w-full text-gray-500 focus:ring-2 focus:ring-blue-500 transition" required />
            </div>

            <h4 className="font-bold text-gray-600 border-b pb-2 pt-4">بيانات ولي الأمر</h4>
            <div className="space-y-4">
                <label htmlFor="parentSelect" className="block text-sm font-medium text-gray-500 mb-1">
                    اختيار ولي أمر موجود أو إضافة جديد
                </label>
                <select
                    id="parentSelect"
                    value={selectedParentId}
                    onChange={handleParentSelect}
                    className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-blue-500 transition"
                >
                    <option value="">-- إضافة ولي أمر جديد --</option>
                    {parents.map(parent => (
                        <option key={parent.id} value={parent.id}>
                            {parent.name} ({parent.phone})
                        </option>
                    ))}
                </select>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input name="fatherName" value={formData.fatherName} onChange={handleChange} placeholder="اسم الأب" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" disabled={!!selectedParentId} />
                <input name="motherName" value={formData.motherName} onChange={handleChange} placeholder="اسم الأم" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" disabled={!!selectedParentId} />
                <input name="fatherPhone" type="tel" value={formData.fatherPhone} onChange={handleChange} placeholder="رقم هاتف الأب" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" disabled={!!selectedParentId} />
                <input name="motherPhone" type="tel" value={formData.motherPhone} onChange={handleChange} placeholder="رقم هاتف الأم" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" disabled={!!selectedParentId} />
            </div>
            <div className="space-y-4">
                <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="البريد الإلكتروني للتواصل" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition" disabled={!!selectedParentId} />
                <div>
                    <label htmlFor="parentPhone" className="block text-sm font-medium text-gray-500 mb-1">
                        كلمة مرور الولي
                    </label>
                    <input
                        id="parentPhone"
                        name="parentPhone"
                        type="text"
                        value={formData.parentPhone || ''}
                        onChange={handleChange}
                        placeholder="أدخل كلمة المرور"
                        className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-blue-500 transition"
                        required
                        disabled={!!selectedParentId}
                    />
                </div>
            </div>

            <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center w-32 disabled:bg-blue-300">
                    {isSubmitting ? <Spinner /> : (initialData ? 'تحديث' : 'حفظ')}
                </button>
            </div>
        </form>
    );
};

const InvoiceModal = ({ show, onClose, fee, student }) => {
    if (!show || !fee || !student) return null;

    const handlePrint = () => {
        const printableContent = document.getElementById('printable-invoice').innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = printableContent;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload(); // To restore event listeners
    };

    return (
        <Modal show={show} onClose={onClose} title="معاينة الفاتورة">
            <div className="p-6" id="printable-invoice" onClick={handlePrint}>
                {/* Header */}
                <div className="flex justify-between items-start mb-8">
                    <div>
                        <img src="/logo192.png" alt="شعار المدرسة" className="h-16 w-auto mb-2" />
                    </div>
                    <h2 className="text-3xl font-bold text-blue-600">فاتورة</h2>
                </div>
                {/* Details */}
                <div className="grid grid-cols-2 gap-8 mb-8">
                    <div>
                        <h3 className="font-bold mb-2 text-gray-600">فاتورة إلى:</h3>
                        <p className="text-gray-800 font-semibold">{student.firstName} {student.lastName}</p>
                        <p className="text-gray-500">ولي الأمر: {student.fatherName}</p>
                        <p className="text-gray-500">الهاتف: {student.fatherPhone}</p>
                    </div>
                    <div className="text-right">
                        <p><span className="font-bold text-gray-600">رقم الفاتورة:</span> #{fee.createdAt ? fee.createdAt.seconds.toString().slice(-8) : fee.id.slice(-8)}</p>
                        <p><span className="font-bold text-gray-600">تاريخ الإصدار:</span> {new Date().toLocaleDateString(APP_LOCALE)}</p>
                        <p><span className="font-bold text-gray-600">تاريخ الخلاص:</span> {new Date(fee.dueDate).toLocaleDateString(APP_LOCALE)}</p>
                    </div>
                </div>
                {/* Table */}
                <table className="w-full mb-8">
                    <thead className="bg-gray-100">
                        <tr>
                            <th className="text-right p-3 font-bold text-gray-600">الوصف</th>
                            <th className="text-center p-3 font-bold text-gray-600">المبلغ</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b">
                            <td className="p-3">{fee.description}</td>
                            <td className="text-center p-3">{fee.amount.toFixed(2)} د.ت</td>
                        </tr>
                    </tbody>
                </table>
                {/* Total */}
                <div className="text-right">
                    <p className="text-xl font-bold text-gray-800">الإجمالي: <span className="text-blue-600">{fee.amount.toFixed(2)} د.ت</span></p>
                    <p className="text-md text-gray-600">+ 7% TVA</p>
                </div>
            </div>
            <div className="flex justify-end p-4 border-t">
                <button onClick={handlePrint} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition flex items-center">
                    <PrintIcon className="w-5 h-5 ml-2" /> طباعة
                </button>
            </div>
        </Modal>
    );
};

const AddTeacherForm = ({ onSave, onDone, initialData, showNotification }) => {
    const [formData, setFormData] = useState({
        name: '', subject: '', email: '', phone: '', photoUrl: '', qualifications: '',
        paymentType: 'monthly', monthlyRate: '', hourlyRate: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                id: initialData.id,
                name: initialData.name || '',
                subject: initialData.subject || '',
                email: initialData.email || '',
                phone: initialData.phone || '',
                photoUrl: initialData.photoUrl || '',
                qualifications: initialData.qualifications || '',
                paymentType: initialData.paymentType || 'monthly',
                monthlyRate: initialData.monthlyRate || '',
                hourlyRate: initialData.hourlyRate || ''
            });
        } else {
            setFormData({
                name: '', subject: '', email: '', phone: '', photoUrl: '', qualifications: '',
                paymentType: 'monthly', monthlyRate: '', hourlyRate: ''
            });
        }
    }, [initialData]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({ ...prev, photoUrl: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.subject) {
            showNotification('الرجاء ملء اسم المعلم والمادة الأساسية.', 'error');
            return;
        }
        setIsSubmitting(true);
        const payload = {
            ...formData,
            monthlyRate: parseFloat(formData.monthlyRate) || 0,
            hourlyRate: parseFloat(formData.hourlyRate) || 0
        };
        await onSave(payload);
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
            <div className="flex items-center gap-4">
                <img
                    src={formData.photoUrl || 'https://placehold.co/100x100/E2E8F0/4A5568?text=صورة'}
                    alt="Preview"
                    className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
                />
                <label className="block">
                    <span className="sr-only">اختر صورة الملف الشخصي</span>
                    <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-green-50 file:text-green-700 hover:file:bg-green-100" />
                </label>
            </div>
            <input name="name" value={formData.name} onChange={handleChange} placeholder="اسم المعلم الكامل" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" required />
            <input name="subject" value={formData.subject} onChange={handleChange} placeholder="المادة الأساسية" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" required />
            <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="البريد الإلكتروني" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" />
            <input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="رقم الهاتف" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" />
            <textarea name="qualifications" value={formData.qualifications} onChange={handleChange} placeholder="المؤهلات والشهادات" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" rows="3"></textarea>
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">نوع الدفع</label>
                <select name="paymentType" value={formData.paymentType} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition">
                    <option value="monthly">شهري</option>
                    <option value="hourly">بالساعة</option>
                </select>
            </div>
            {formData.paymentType === 'monthly' ? (
                <input name="monthlyRate" type="number" value={formData.monthlyRate} onChange={handleChange} placeholder="الراتب الشهري" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" />
            ) : (
                <input name="hourlyRate" type="number" value={formData.hourlyRate} onChange={handleChange} placeholder="الأجر بالساعة" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-green-500 transition" />
            )}

            <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting} className="bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition flex items-center justify-center w-32 disabled:bg-green-300">
                    {isSubmitting ? <Spinner /> : (initialData ? 'تحديث' : 'حفظ')}
                </button>
            </div>
        </form>
    );
};

const AddFeeForm = ({ students, onAdd, onDone, showNotification }) => {
    const months = [
        "جانفي", "فيفري", "مارس", "أفريل", "ماي", "جوان",
        "جويلية", "أوت", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
    ];
    const [studentId, setStudentId] = useState('');
    const [dueDate, setDueDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString().split('T')[0]);
    const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [feeItems, setFeeItems] = useState([
        { description: "رسوم شهر", amount: '' }
    ]);

    const addFeeItem = () => {
        setFeeItems([...feeItems, { description: 'رسوم إضافية', amount: '' }]);
    };

    const removeFeeItem = (index) => {
        setFeeItems(feeItems.filter((_, i) => i !== index));
    };

    const updateFeeItem = (index, field, value) => {
        const newItems = [...feeItems];
        if (field === 'description' && index === 0) {
            // Prevent editing description for the first item
            return;
        }
        newItems[index][field] = value;
        setFeeItems(newItems);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!studentId || !dueDate || !selectedMonth || feeItems.some(item => !item.description || !item.amount)) {
            showNotification("الرجاء ملء جميع الحقول.", 'error');
            return;
        }

        // Validate dueDate is not in the past
        try {
            const due = new Date(dueDate);
            const today = new Date();
            due.setHours(0, 0, 0, 0);
            today.setHours(0, 0, 0, 0);
            if (due < today) {
                showNotification('تاريخ الخلاص لا يمكن أن يكون في الماضي.', 'error');
                return;
            }
        } catch (err) {
            // ignore parse errors and let server handle if necessary
        }

        setIsSubmitting(true);

        // Calculate total amount
        const totalAmount = feeItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

        // Create description from all items
        // Get additional fees description (excluding the first item which is monthly fee)
        const additionalFees = feeItems.slice(1)
            .map(item => `${item.description}: ${item.amount}`)
            .join('، ');

        // Format description: First item amount + (additional fees if any)
        const mainFeeAmount = feeItems[0].amount;
        const descriptionInParentheses = additionalFees ? `${mainFeeAmount}، ${additionalFees}` : mainFeeAmount;

        await onAdd({
            studentId,
            amount: totalAmount,
            dueDate,
            description: `رسوم شهر ${selectedMonth} (${descriptionInParentheses})`,
            status: 'unpaid',
            feeItems: feeItems.map(item => ({
                ...item,
                amount: parseFloat(item.amount)
            }))
        });

        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required>
                <option value="">-- اختر تلميذ --</option>
                {students.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required>
                {months.map(month => <option key={month} value={month}>{month}</option>)}
            </select>
            <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="p-3 border rounded-lg w-full text-gray-500 focus:ring-2 focus:ring-green-500 transition" required />

            <div className="space-y-4">
                {feeItems.map((item, index) => (
                    <div key={index} className="flex space-x-4 space-x-reverse">
                        <input
                            type="text"
                            value={item.description}
                            onChange={(e) => updateFeeItem(index, 'description', e.target.value)}
                            placeholder="وصف الرسوم (مثال: رسوم التسجيل)"
                            className="p-3 border rounded-lg flex-grow focus:ring-2 focus:ring-green-500 transition"
                            required
                            readOnly={index === 0} // Make the first item's description read-only
                        />
                        <input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateFeeItem(index, 'amount', e.target.value)}
                            placeholder="المبلغ"
                            className="p-3 border rounded-lg w-32 focus:ring-2 focus:ring-green-500 transition"
                            required
                        />
                        {feeItems.length > 1 && (
                            <button
                                type="button"
                                onClick={() => removeFeeItem(index)}
                                className="p-2 text-red-600 hover:text-red-700"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                ))}
            </div>

            <div className="flex justify-between items-center">
                <button
                    type="button"
                    onClick={addFeeItem}
                    className="text-green-600 hover:text-green-700 font-medium"
                >
                    + إضافة رسوم أخرى
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition flex items-center justify-center w-32 disabled:bg-green-300"
                >
                    {isSubmitting ? <Spinner /> : 'إضافة رسم'}
                </button>
            </div>
        </form>
    );
};

const AddClassForm = ({ onSave, onDone, initialData, showNotification }) => {
    const [formData, setFormData] = useState({ name: '', level: '', category: 'أساسي', order: 0 });

    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name,
                level: initialData.level,
                category: initialData.category || 'أساسي',
                order: typeof initialData.order !== 'undefined' ? initialData.order : 0
            });
        } else {
            setFormData({ name: '', level: '', category: 'أساسي', order: 0 });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: name === 'order' ? parseInt(value || '0', 10) : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.name || !formData.level || !formData.category) {
            showNotification('الرجاء ملء جميع الحقول.', 'error');
            return;
        }
        // ensure order is a number
        const payload = { ...formData, order: Number(formData.order || 0) };
        onSave(payload);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input name="name" value={formData.name} onChange={handleChange} placeholder="اسم القسم (مثال: 7 أساسي أ)" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-indigo-500 transition" required />
            <input name="level" value={formData.level} onChange={handleChange} placeholder="المستوى (مثال: السنة السابعة)" className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-indigo-500 transition" required />
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">فئة الفصل</label>
                <select name="category" value={formData.category} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-indigo-500 transition">
                    <option value="أساسي">أساسي</option>
                    <option value="ثانوي">ثانوي</option>
                    <option value="تكوين">تكوين</option>
                </select>
            </div>
            <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">ترتيب الظهور (رقم)</label>
                <input name="order" type="number" value={formData.order} onChange={handleChange} className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-indigo-500 transition" min="0" />
                <p className="text-xs text-gray-500 mt-1">قيمة أصغر تظهر أولاً؛ اترك 0 لترتيب افتراضي.</p>
            </div>
            <div className="flex justify-end pt-2">
                <button type="submit" className="bg-indigo-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-indigo-700 transition">
                    {initialData ? 'تحديث' : 'حفظ'}
                </button>
            </div>
        </form>
    );
};

const AddEditSubjectForm = ({ onSave, onDone, initialData, showNotification }) => {
    const [name, setName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setName(initialData.name || '');
        } else {
            setName('');
        }
    }, [initialData]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            showNotification('الرجاء إدخال اسم المادة.', 'error');
            return;
        }
        setIsSubmitting(true);
        await onSave({ ...initialData, name });
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="اسم المادة (مثال: الرياضيات)"
                className="p-3 border rounded-lg w-full focus:ring-2 focus:ring-amber-500 transition"
                required
            />
            <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting} className="bg-amber-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-amber-700 transition flex items-center justify-center w-32 disabled:bg-amber-300">
                    {isSubmitting ? <Spinner /> : (initialData ? 'تحديث' : 'حفظ')}
                </button>
            </div>
        </form>
    );
};

const AddEditSessionForm = ({ onSave, onDone, initialData, classes, teachers, subjects, showNotification }) => {
    const [formData, setFormData] = useState({
        day: 'الإثنين',
        time: '08:00 - 09:00',
        subjectId: '',
        teacherId: '',
        classId: ''
    });

    useEffect(() => {
        if (initialData) {
            setFormData({ ...initialData });
        } else if (classes.length > 0) {
            setFormData(prev => ({ ...prev, classId: classes[0].id }));
        }
    }, [initialData, classes]);

    const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!formData.subjectId || !formData.teacherId || !formData.classId) {
            showNotification('الرجاء ملء جميع الحقول.', 'error');
            return;
        }
        onSave(formData);
        onDone();
    };

    const days = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const times = ['08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00'];

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <select name="classId" value={formData.classId} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white" required>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-4">
                <select name="day" value={formData.day} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white" required>
                    {days.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                <select name="time" value={formData.time} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white" required>
                    {times.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
            </div>
            <select name="subjectId" value={formData.subjectId} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white" required>
                <option value="">-- اختر المادة --</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select name="teacherId" value={formData.teacherId} onChange={handleChange} className="p-3 border rounded-lg w-full bg-white" required>
                <option value="">-- اختر المعلم --</option>
                {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div className="flex justify-end pt-2">
                <button type="submit" className="bg-purple-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-purple-700 transition">
                    {initialData ? 'تحديث الحصة' : 'إضافة الحصة'}
                </button>
            </div>
        </form>
    );
};

const EnrollStudentForm = ({ students, classes, enrollments, onEnroll, onDone, showNotification }) => {
    const [studentId, setStudentId] = useState('');
    const [category, setCategory] = useState('');
    const [level, setLevel] = useState('');
    const [classId, setClassId] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const enrolledStudentIds = new Set(enrollments.map(e => e.studentId));
    const unenrolledStudents = students.filter(s => !enrolledStudentIds.has(s.id));

    // categories available from classes (fallback to both options)
    const derivedCategories = [...new Set(classes.map(c => c.category || 'أساسي'))];
    const defaultCategories = ['أساسي', 'ثانوي', 'تكوين'];
    const categories = derivedCategories.length > 0 ? Array.from(new Set([...derivedCategories, ...defaultCategories])) : defaultCategories;

    // levels filtered by selected category
    const levels = [...new Set(classes.filter(c => !category || (c.category || 'أساسي') === category).map(c => c.level))];
    const filteredClasses = level ? classes.filter(c => c.level === level && (!category || (c.category || 'أساسي') === category)) : [];

    const handleCategoryChange = (e) => {
        setCategory(e.target.value);
        setLevel('');
        setClassId('');
    };

    const handleLevelChange = (e) => {
        setLevel(e.target.value);
        setClassId(''); // Reset class selection when level changes
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!studentId || !classId || !level || !category) {
            showNotification('الرجاء تعبئة جميع الحقول.', 'error');
            return;
        }
        setIsSubmitting(true);
        await onEnroll({ studentId, classId, category });
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <select value={studentId} onChange={(e) => setStudentId(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required>
                <option value="">-- اختر تلميذ --</option>
                {unenrolledStudents.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
            </select>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">فئة الفصل</label>
                    <select value={category} onChange={handleCategoryChange} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required>
                        <option value="">-- اختر فئة --</option>
                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">المستوى</label>
                    <select value={level} onChange={handleLevelChange} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required disabled={!category}>
                        <option value="">-- اختر مستوى --</option>
                        {levels.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
            </div>

            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-green-500 transition" required disabled={!level}>
                <option value="">-- اختر قسم --</option>
                {filteredClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting} className="bg-green-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-green-700 transition flex items-center justify-center w-32 disabled:bg-green-300">
                    {isSubmitting ? <Spinner /> : 'تسجيل'}
                </button>
            </div>
        </form>
    );
};

const EditEnrollmentForm = ({ student, classes, enrollment, onSave, onDone }) => {
    const [classId, setClassId] = useState(enrollment?.classId || '');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!classId) return;
        setIsSubmitting(true);
        await onSave(enrollment.id, classId);
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-gray-700">التلميذ</label>
                <p className="mt-1 p-3 border rounded-lg bg-gray-100">{student.firstName} {student.lastName}</p>
            </div>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-purple-500 transition" required>
                <option value="">-- اختر قسم --</option>
                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>

            <div className="flex justify-end pt-2">
                <button type="submit" disabled={isSubmitting} className="bg-purple-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-purple-700 transition flex items-center justify-center w-32 disabled:bg-purple-300">
                    {isSubmitting ? <Spinner /> : 'تحديث'}
                </button>
            </div>
        </form>
    );
};


// --- Page-level components ---
const StudentDetailSidebar = ({ student, studentClass, fees, onClose, showNotification, timetable, subjects, teachers, onEditEnrollment }) => {
    if (!student) return null;

    const classTimetable = timetable ? timetable.filter(s => s.classId === studentClass?.id) : [];
    const teacherMap = new Map(teachers.map(t => [t.id, t]));
    const subjectMap = new Map(subjects.map(s => [s.id, s]));
    const days = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const times = ['08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00'];

    const DetailItem = ({ label, value }) => (
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            <p className="font-semibold text-gray-800">{value || '-'}</p>
        </div>
    );

    return (
        <div className="fixed top-0 left-0 h-full w-full md:w-1/3 bg-white shadow-2xl z-40 p-6 overflow-y-auto slide-in-right">
            <div className="flex justify-between items-center border-b pb-4 mb-6">
                <h3 className="text-xl font-bold text-gray-800">ملف التلميذ</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-red-500"><XMarkIcon className="w-6 h-6" /></button>
            </div>

            <div className="text-center mb-6">
                <img src={student.photoUrl} alt={`${student.firstName} ${student.lastName}`} className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-blue-200" />
                <h4 className="text-2xl font-bold">{student.firstName} {student.lastName}</h4>
                <div className="flex items-center justify-center gap-2 mt-1">
                    <p className="text-gray-500">{studentClass?.name || 'غير مسجل'}</p>
                    {studentClass && (
                        <button onClick={onEditEnrollment} className="text-blue-500 hover:text-blue-700 p-1 rounded-full hover:bg-blue-100" title="تعديل الصف">
                            <PencilIcon className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            <div className="space-y-6">
                <div>
                    <h5 className="font-bold mb-3 text-gray-700 border-b pb-2">البيانات الشخصية</h5>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                        <DetailItem label="تاريخ الميلاد" value={student.birthDate} />
                        <DetailItem label="الرقم الدراسي (للدخول)" value={student.studentId} />
                        <DetailItem label="ولي الأمر" value={student.fatherName} />
                        <DetailItem label="هاتف ولي الأمر" value={student.fatherPhone} />
                        <DetailItem label="البريد الإلكتروني" value={student.email} />
                        <DetailItem label="تاريخ التسجيل" value={student.registrationDate?.toDate().toLocaleDateString(APP_LOCALE) || '-'} />
                    </div>
                </div>

                <div>
                    <h5 className="font-bold mb-3 text-gray-700 border-b pb-2">الجدول الدراسي</h5>
                    {studentClass ? (
                        <div className="overflow-x-auto text-xs">
                            <table className="w-full text-center border-collapse">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="p-1 border">الوقت</th>
                                        {days.slice(0, 5).map(day => <th key={day} className="p-1 border">{day.substring(0, 3)}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {times.map(time => (
                                        <tr key={time}>
                                            <td className="p-1 border font-mono">{time.substring(0, 5)}</td>
                                            {days.slice(0, 5).map(day => {
                                                const session = classTimetable.find(s => s.day === day && s.time === time);
                                                return (
                                                    <td key={day} className={`p-1 border ${session ? 'bg-blue-50' : ''}`}>
                                                        {session ? (
                                                            <div>
                                                                <p className="font-semibold">{subjectMap.get(session.subjectId)?.name}</p>
                                                                <p className="text-gray-500">{teacherMap.get(session.teacherId)?.name.split(' ')[1]}</p>
                                                            </div>
                                                        ) : '-'}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center p-4">التلميذ غير مسجل في أي قسم لعرض الجدول.</p>
                    )}
                </div>

                <div>
                    <h5 className="font-bold mb-3 text-gray-700 border-b pb-2">الوضع المالي</h5>
                    <ul className="space-y-2 text-sm">
                        {fees.map(fee => (
                            <li key={fee.id} className="flex justify-between items-center p-2 rounded-lg bg-gray-50">
                                <span>{fee.description}</span>
                                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${fee.status === 'paid' ? 'text-green-800 bg-green-200' :
                                        fee.status === 'unpaid' ? 'text-yellow-800 bg-yellow-200' : 'text-red-800 bg-red-200'
                                    }`}>{fee.amount.toFixed(2)} د.ت</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="border-t pt-6 flex flex-col sm:flex-row gap-2">
                    <button onClick={() => showNotification("جاري طباعة التقرير...", "info")} className="flex-1 bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition flex items-center justify-center">
                        <PrintIcon className="w-5 h-5 ml-2" /> طباعة تقرير
                    </button>
                    <button onClick={() => showNotification("تم إرسال الإشعار!", "success")} className="flex-1 bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center justify-center">
                        <EnvelopeIcon className="w-5 h-5 ml-2" /> إرسال إشعار
                    </button>
                </div>
            </div>
        </div>
    );
}

const StudentManagementPage = ({ students, classes, enrollments, fees, onUpdateStudent, onDeleteStudent, showNotification, onAddStudent, onAddEnrollment, onUpdateEnrollment, timetable, subjects, teachers, parents }) => {
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [classFilter, setClassFilter] = useState('');
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showEnrollModal, setShowEnrollModal] = useState(false);
    const [showEditEnrollmentModal, setShowEditEnrollmentModal] = useState(false);
    const [studentToPrint, setStudentToPrint] = useState(null);

    const classMap = new Map(classes.map(c => [c.id, c]));
    const enrollmentMap = new Map(enrollments.map(e => [e.studentId, e]));

    const getStudentDetails = (student) => {
        const enrollment = enrollmentMap.get(student.id);
        const studentClass = enrollment ? classMap.get(enrollment.classId) : null;
        const studentFees = fees.filter(f => f.studentId === student.id);
        const totalDue = studentFees.filter(f => f.status !== 'paid').reduce((sum, f) => sum + f.amount, 0);
        return { ...student, studentClass, studentFees, totalDue };
    };

    const detailedStudents = students.map(getStudentDetails);

    const filteredStudents = detailedStudents.filter(student => {
        const matchesSearchTerm =
            `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            student.id.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesClassFilter =
            !classFilter || student.studentClass?.id === classFilter;

        return matchesSearchTerm && matchesClassFilter;
    });

    useEffect(() => {
        if (studentToPrint) {
            const printContent = document.getElementById('printable-student-report');
            if (printContent) {
                // استخراج بيانات التلميذ
                const { firstName, lastName, photoUrl, studentClass, studentFees } = studentToPrint;
                // تعريف جدول المواد الدراسية
                let subjectsTable = '';
                const header = `<h2 style='color:#2563eb;margin-bottom:10px;'>تقرير التلميذ</h2>`;
                const infoTable = `
                    <table style='width:100%;margin-bottom:20px;border:1px solid #eee;border-radius:8px;background:#f9fafb;'>
                        <tr>
                            <td rowspan='3' style='width:120px;padding:10px;text-align:center;'>
                                <img src='${photoUrl}' alt='صورة التلميذ' style='width:80px;height:80px;border-radius:50%;border:2px solid #2563eb;object-fit:cover;'/>
                            </td>
                            <td style='font-size:20px;font-weight:bold;color:#222;padding:10px;'>${firstName} ${lastName}</td>
                        </tr>
                        <tr>
                            <td style='color:#555;padding:10px;'>الصف: <span style='font-weight:bold;color:#2563eb;'>${studentClass?.name || '-'}</span></td>
                        </tr>
                    </table>
                `;
                // جدول دراسي أسبوعي بنفس تنسيق قسم الجدول الدراسي
                let weeklyTable = '';
                if (studentClass && timetable && Array.isArray(timetable)) {
                    const days = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
                    // استخراج جميع الأوقات من جدول الحصص لهذا الصف
                    const classTimetable = timetable.filter(s => s.classId === studentClass.id);
                    const times = ['08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00'];
                    if (classTimetable.length > 0) {
                        weeklyTable = `
                            <h4 style='margin-top:30px;color:#2563eb;'>الجدول الدراسي الأسبوعي</h4>
                            <table style='width:100%;border-collapse:collapse;margin-bottom:20px;'>
                                <thead>
                                    <tr style='background:#f2f6ff;'>
                                        <th style='padding:8px;border:1px solid #ddd;'>الوقت</th>
                                        ${days.map(day => `<th style='padding:8px;border:1px solid #ddd;'>${day}</th>`).join('')}
                                    </tr>
                                </thead>
                                <tbody>
                                    ${times.map(time => `
                                        <tr>
                                            <td style='padding:8px;border:1px solid #ddd;'>${time}</td>
                                            ${days.map(day => {
                            const session = classTimetable.find(s => s.day === day && s.time === time);
                            return `<td style='padding:8px;border:1px solid #ddd;'>${session ? (session.subjectName || '') + '<br/>' + (session.teacherName || '') : '-'}</td>`;
                        }).join('')}
                                        </tr>
                                    `).join('')}
                                </tbody>
                            </table>
                        `;
                    } else {
                        weeklyTable = `<h4 style='margin-top:30px;color:#2563eb;'>الجدول الدراسي الأسبوعي</h4><div>لا يوجد جدول لهذا الصف.</div>`;
                    }
                }
                const printableHtml = `
                    <html>
                    <head>
                        <title>تقرير التلميذ</title>
                        <style>
                            @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
                            body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 30px; background: #f3f4f6; }
                            .print-header { text-align: center; margin-bottom: 20px; }
                            h2, h4 { font-family: 'Cairo', sans-serif; }
                            table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 15px; }
                            th, td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                            th { background-color: #f2f6ff; }
                        </style>
                    </head>
                    <body>
                        <div class="print-header">${header}</div>
                        ${infoTable}
                        ${subjectsTable}
                        ${weeklyTable}
                    </body>
                    </html>
                `;
                const printWindow = window.open('', '_blank');
                printWindow.document.write(printableHtml);
                printWindow.document.close();
                printWindow.focus();
                setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                    setStudentToPrint(null);
                }, 250);
            }
        }
    }, [studentToPrint, timetable, subjects, teachers]);


    const handleEditClick = (student) => {
        setSelectedStudent(student);
        setShowEditModal(true);
    };

    const handleEditEnrollmentClick = () => {
        if (selectedStudent && enrollments.some(e => e.studentId === selectedStudent.id)) {
            setShowEditEnrollmentModal(true);
        } else {
            showNotification("هذا التلميذ غير مسجل في أي صف حاليًا. استخدم زر 'تسجيل بالفصول'.", "info");
        }
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ show: false }))}
            />
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-2 w-full sm:w-auto sm:flex-1">
                    <div className="relative flex-grow">
                        <input
                            type="text"
                            placeholder="بحث بالاسم أو الرقم الدراسي..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="p-2 border rounded-lg w-full pr-10 focus:ring-2 focus:ring-blue-500 transition"
                        />
                        <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    <select
                        value={classFilter}
                        onChange={(e) => setClassFilter(e.target.value)}
                        className="p-2 border rounded-lg w-full sm:w-auto bg-white focus:ring-2 focus:ring-blue-500 transition"
                    >
                        <option value="">كل الصفوف</option>
                        {classes.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                    <button
                        onClick={() => {
                            const selectedClass = classes.find(c => c.id === classFilter);
                            if (!selectedClass) {
                                showNotification('يرجى اختيار صف أولاً', 'warning');
                                return;
                            }
                            const studentsInClass = detailedStudents.filter(s => s.studentClass?.id === classFilter);
                            if (studentsInClass.length === 0) {
                                showNotification('لا يوجد تلامذة في هذا الصف', 'info');
                                return;
                            }
                            // ترتيب تنازلي حسب الاسم الأخير
                            const sorted = [...studentsInClass].sort((a, b) => b.lastName.localeCompare(a.lastName));
                            const tableRows = sorted.map((s, i) => `
                                    <tr>
                                        <td style='border:1px solid #ccc;padding:8px;text-align:center;'>${i + 1}</td>
                                        <td style='border:1px solid #ccc;padding:8px;'>${s.firstName} ${s.lastName}</td>
                                        <td style='border:1px solid #ccc;padding:8px;'></td>
                                    </tr>
                                `).join('');
                            const printHtml = `
                                    <html>
                                    <head>
                                        <title>طباعة كشف حضور التلامذة</title>
                                        <style>
                                            body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 20px; }
                                            h2 { text-align: center; margin-bottom: 20px; }
                                            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 18px; }
                                            th, td { border: 1px solid #ccc; padding: 8px; }
                                            th { background: #f2f6ff; }
                                        </style>
                                    </head>
                                    <body>
                                        <h2>كشف حضور وغياب التلامذة في صف: ${selectedClass.name}</h2>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>الرقم</th>
                                                    <th>اسم الطالب</th>
                                                    <th>الحضور / الغياب</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                ${tableRows}
                                            </tbody>
                                        </table>
                                    </body>
                                    </html>
                                `;
                            const win = window.open('', '_blank');
                            win.document.write(printHtml);
                            win.document.close();
                            win.focus();
                            setTimeout(() => {
                                win.print();
                                win.close();
                            }, 250);
                        }}
                        className="bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition flex items-center"
                        style={{ marginRight: '8px' }}
                        title="طباعة أسماء التلامذة في الصف المحدد"
                    >
                        <PrintIcon className="w-5 h-5 ml-2" /> طباعة أسماء التلامذة
                    </button>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-2">
                    <button onClick={() => setShowEnrollModal(true)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center">
                        <ClipboardListIcon className="w-5 h-5 ml-2" /> تسجيل بالفصول
                    </button>
                    <button onClick={() => setShowAddModal(true)} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center">
                        <UserPlusIcon className="w-5 h-5 ml-2" /> إضافة طالب
                    </button>
                </div>
            </div>

            <div className="relative">
                <div className="bg-white p-4 rounded-xl shadow-md overflow-x-auto">
                    <table className="w-full text-right min-w-[800px]">
                        <thead className="border-b-2 text-gray-600">
                            <tr>
                                <th className="p-3">الصورة</th>
                                <th className="p-3">الاسم</th>
                                <th className="p-3">الرقم الدراسي</th>
                                <th className="p-3">الصف</th>
                                <th className="p-3">الرسوم</th>
                                <th className="p-3">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStudents.map((student, index) => (
                                <tr key={student.id} className="border-b hover:bg-blue-50">
                                    <td className="p-2"><img src={student.photoUrl} alt={student.firstName} className="w-10 h-10 rounded-full object-cover" /></td>
                                    <td className="p-3 font-semibold">
                                        <button onClick={() => setSelectedStudent(student)} className="text-left text-blue-600 hover:underline">
                                            {student.firstName} {student.lastName}
                                        </button>
                                    </td>
                                    <td className="p-3 text-gray-500 font-mono text-sm">{index + 1}</td>
                                    <td className="p-3 text-gray-600">{student.studentClass?.name || '-'}</td>
                                    <td className="p-3">
                                        {student.totalDue > 0 ?
                                            <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">للخلاص</span> :
                                            <span className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-200 rounded-full">مسددة</span>
                                        }
                                    </td>
                                    <td className="p-3 flex items-center space-x-1 rtl:space-x-reverse" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => handleEditClick(student)} className="text-green-500 hover:text-green-700 p-1 rounded-full hover:bg-green-100" title="تعديل">
                                            <PencilIcon className="w-5 h-5" />
                                        </button>
                                        <button
                                            onClick={() => setConfirmModal({
                                                show: true,
                                                message: 'هل أنت متأكد أنك تريد حذف هذا الطالب؟ لا يمكن التراجع عن العملية.',
                                                onConfirm: () => {
                                                    onDeleteStudent(student.id);
                                                    setConfirmModal({ show: false });
                                                },
                                                onCancel: () => setConfirmModal({ show: false })
                                            })}
                                            className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100"
                                            title="حذف"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                        {/* زر الطباعة تم حذفه بناءً على طلب المستخدم */}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {studentToPrint &&
                    <div id="printable-student-report" style={{ display: 'none' }}>
                        <StudentDetailSidebar
                            student={studentToPrint}
                            studentClass={studentToPrint.studentClass}
                            fees={studentToPrint.studentFees}
                            onClose={() => { }}
                            showNotification={() => { }}
                            timetable={timetable}
                            subjects={subjects}
                            teachers={teachers}
                            onEditEnrollment={() => { }}
                        />
                    </div>
                }
            </div>

            <Modal show={showEnrollModal} onClose={() => setShowEnrollModal(false)} title="تسجيل تلميذ في فصل">
                <EnrollStudentForm
                    students={students}
                    classes={classes}
                    enrollments={enrollments}
                    onEnroll={onAddEnrollment}
                    onDone={() => setShowEnrollModal(false)}
                    showNotification={showNotification}
                />
            </Modal>

            {/* Student detail sidebar */}
            {selectedStudent && (
                <StudentDetailSidebar
                    student={selectedStudent}
                    studentClass={classMap.get(enrollmentMap.get(selectedStudent.id)?.classId)}
                    fees={fees.filter(f => f.studentId === selectedStudent.id)}
                    onClose={() => setSelectedStudent(null)}
                    showNotification={showNotification}
                    timetable={timetable}
                    subjects={subjects}
                    teachers={teachers}
                    onEditEnrollment={() => setShowEditEnrollmentModal(true)}
                />
            )}

            {selectedStudent && (
                <Modal show={showEditEnrollmentModal} onClose={() => setShowEditEnrollmentModal(false)} title="تعديل صف التلميذ">
                    <EditEnrollmentForm
                        student={selectedStudent}
                        classes={classes}
                        enrollment={enrollments.find(e => e.studentId === selectedStudent.id)}
                        onSave={onUpdateEnrollment}
                        onDone={() => setShowEditEnrollmentModal(false)}
                    />
                </Modal>
            )}

            <Modal show={showAddModal} onClose={() => setShowAddModal(false)} title="إضافة طالب جديد">
                <AddStudentForm onSave={onAddStudent} onDone={() => setShowAddModal(false)} showNotification={showNotification} parents={parents} />
            </Modal>
            <Modal show={showEditModal} onClose={() => setShowEditModal(false)} title="تعديل بيانات الطالب">
                <AddStudentForm onSave={onUpdateStudent} onDone={() => setShowEditModal(false)} initialData={selectedStudent} showNotification={showNotification} parents={parents} />
            </Modal>
        </div>
    );
};

const AcademicYearProgress = ({ settings }) => {
    if (!settings || !settings.academicYearStart || !settings.academicYearEnd) {
        return null;
    }

    const startDate = new Date(settings.academicYearStart);
    const endDate = new Date(settings.academicYearEnd);
    const today = new Date('2025-09-01T20:37:00'); // Date from user context

    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsedDuration = today.getTime() - startDate.getTime();

    let progressPercentage = 0;
    if (totalDuration > 0) {
        progressPercentage = Math.round((elapsedDuration / totalDuration) * 100);
    }
    progressPercentage = Math.max(0, Math.min(100, progressPercentage));

    const remainingDays = Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24)));

    return (
        <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-gray-700 mb-4 text-xl">التقدم في العام الدراسي</h3>
            <div className="space-y-4">
                <div className="flex justify-between items-center text-sm font-semibold text-gray-600">
                    <span>بداية العام: {startDate.toLocaleDateString(APP_LOCALE)}</span>
                    <span>نهاية العام: {endDate.toLocaleDateString(APP_LOCALE)}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                    <div
                        className="bg-blue-600 h-4 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${progressPercentage}%` }}
                    ></div>
                </div>
                <div className="flex justify-between items-center text-sm">
                    <span className="font-bold text-blue-700">{progressPercentage}% مكتمل</span>
                    <span className="text-gray-500">{remainingDays} يومًا متبقيًا</span>
                </div>
            </div>
        </div>
    );
};

const HomePage = ({ stats, setPage, settings }) => {

    const StatCard = ({ title, value, icon, color }) => (
        <div className="bg-white p-5 rounded-xl shadow-md flex items-center space-x-4 rtl:space-x-reverse">
            <div className={`p-3 rounded-full ${color}`}>
                {icon}
            </div>
            <div>
                <p className="text-gray-500 text-sm">{title}</p>
                <p className="text-2xl font-bold text-gray-800">{value}</p>
            </div>
        </div>
    );

    const FeatureCard = ({ title, description, icon, color, onClick }) => (
        <button
            onClick={onClick}
            className={`card p-6 rounded-xl text-right flex flex-col justify-between h-full bg-white shadow-md border-b-4 ${color.border} hover:bg-gray-50`}
        >
            <div>
                <div className={`p-3 rounded-full inline-block ${color.bg}`}>
                    {icon}
                </div>
                <h3 className="font-bold text-xl text-gray-800 mt-4">{title}</h3>
                <p className="text-gray-500 text-sm mt-1">{description}</p>
            </div>
        </button>
    );

    return (
        <div className="p-4 md:p-8 space-y-8 fade-in-up">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <StatCard title="عدد التلامذة الحاليين" value={stats.students} icon={<UsersGroupIcon className="w-8 h-8 text-blue-600" />} color="bg-blue-100" />
                    <StatCard title="عدد المعلمين" value={stats.teachers} icon={<AcademicCapIcon className="w-8 h-8 text-green-600" />} color="bg-green-100" />
                    <StatCard title="عدد الأقسام" value={stats.classes} icon={<LibraryIcon className="w-8 h-8 text-indigo-600" />} color="bg-indigo-100" />
                    <StatCard title="الأجور المستحقة" value={`${stats.unpaidFees.toFixed(2)} د.ت`} icon={<DollarSignIcon className="w-8 h-8 text-red-600" />} color="bg-red-100" />
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md">
                    <h3 className="font-bold text-gray-700 mb-4 flex items-center"><BellIcon className="w-5 h-5 ml-2 text-yellow-500" /> تنبيهات مهمة</h3>
                    <ul className="space-y-3 text-sm">
                        {stats.overdueStudents > 0 && (
                            <li className="flex items-start">
                                <ExclamationTriangleIcon className="w-4 h-4 text-red-500 mt-1 ml-2 flex-shrink-0" />
                                <button onClick={() => setPage('finance')} className="text-right hover:underline focus:outline-none">
                                    <div><span className="font-bold">{stats.overdueStudents} تلامذة</span> لديهم أجور متأخرة.</div>
                                </button>
                            </li>
                        )}
                        {stats.newStudents > 0 && (
                            <li className="flex items-start">
                                <ExclamationTriangleIcon className="w-4 h-4 text-blue-500 mt-1 ml-2 flex-shrink-0" />
                                <button onClick={() => setPage('studentManagement')} className="text-right hover:underline focus:outline-none">
                                    <div>تم تسجيل <span className="font-bold">{stats.newStudents} تلامذة</span> جدد هذا الأسبوع.</div>
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            </div>

            <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">لوحة التحكم</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <FeatureCard title={t('pageTitles.studentManagement', TRANSLATIONS.ar.pageTitles.studentManagement)} description={t('labels.manageStudentsDesc', 'Manage student records and enrollment.')} icon={<UsersGroupIcon className="w-8 h-8 text-blue-500" />} color={{ bg: 'bg-blue-100', border: 'border-blue-500' }} onClick={() => setPage('studentManagement')} />
                    <FeatureCard title={t('pageTitles.teacherManagement', TRANSLATIONS.ar.pageTitles.teacherManagement)} description={t('labels.manageTeachersDesc', 'Profiles, schedules, and evaluations.')} icon={<AcademicCapIcon className="w-8 h-8 text-green-500" />} color={{ bg: 'bg-green-100', border: 'border-green-500' }} onClick={() => setPage('teacherManagement')} />
                    <FeatureCard title="إدارة الأقسام" description="إنشاء وتعديل الأقسام الدراسية." icon={<LibraryIcon className="w-8 h-8 text-indigo-500" />} color={{ bg: 'bg-indigo-100', border: 'border-indigo-500' }} onClick={() => setPage('classManagement')} />
                    <FeatureCard title="إدارة الواجبات" description="إضافة وتعديل الواجبات المدرسية." icon={<ClipboardDocumentCheckIcon className="w-8 h-8 text-teal-500" />} color={{ bg: 'bg-teal-100', border: 'border-teal-500' }} onClick={() => setPage('homeworkManagement')} />
                    <FeatureCard title="الإدارة المالية" description="متابعة الأجور والفواتير." icon={<DollarSignIcon className="w-8 h-8 text-red-500" />} color={{ bg: 'bg-red-100', border: 'border-red-500' }} onClick={() => setPage('finance')} />
                    <FeatureCard title="إدارة الإشعارات" description="إرسال وإدارة إشعارات الفصول." icon={<BellIcon className="w-8 h-8 text-yellow-500" />} color={{ bg: 'bg-yellow-100', border: 'border-yellow-500' }} onClick={() => setPage('notifications')} />
                </div>
            </div>

            {/* تم إزالة مكون التقدم في العام الدراسي */}
        </div>
    );
};

const TeacherDetailSidebar = ({ teacher, schedule, onClose, showNotification }) => {
    if (!teacher) return null;

    const DetailItem = ({ label, value, icon }) => (
        <div className="flex items-start">
            <div className="text-gray-400 w-5 h-5 mr-2 mt-1">{icon}</div>
            <div>
                <p className="text-sm text-gray-500">{label}</p>
                <p className="font-semibold text-gray-800">{value || '-'}</p>
            </div>
        </div>
    );

    const performanceData = {
        rating: 4.8,
        studentEngagement: '92%',
        parentFeedback: 'إيجابي'
    };

    return (
        <div className="fixed top-0 left-0 h-full w-full md:w-1/3 bg-white shadow-2xl z-40 p-6 overflow-y-auto slide-in-right">
            <div className="flex justify-between items-center border-b pb-4 mb-6">
                <h3 className="text-xl font-bold text-gray-800">ملف المعلم</h3>
                <button onClick={onClose} className="text-gray-500 hover:text-red-500"><XMarkIcon className="w-6 h-6" /></button>
            </div>

            <div className="text-center mb-6">
                <img src={teacher.photoUrl} alt={teacher.name} className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-green-200" />
                <h4 className="text-2xl font-bold text-gray-800">{teacher.name}</h4>
                <p className="text-green-600 font-semibold">{teacher.subject}</p>
            </div>

            <div className="space-y-6">
                <div>
                    <h5 className="font-bold mb-3 text-gray-700 border-b pb-2">المعلومات الشخصية</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                        <DetailItem label="البريد الإلكتروني" value={teacher.email} icon={<EnvelopeIcon />} />
                        <DetailItem label="رقم الهاتف" value={teacher.phone} icon={<BriefcaseIcon />} />
                        <DetailItem label="تاريخ الانضمام" value={teacher.joinDate?.toDate().toLocaleDateString(APP_LOCALE)} icon={<CalendarIcon />} />
                        <DetailItem label="المؤهلات" value={teacher.qualifications} icon={<AcademicCapIcon />} />
                    </div>
                </div>



                <div>
                    <h5 className="font-bold mb-3 text-gray-700 border-b pb-2">الجدول الدراسي</h5>
                    <ul className="space-y-2 text-sm max-h-48 overflow-y-auto">
                        {schedule.length > 0 ? schedule.map((item, index) => (
                            <li key={index} className="flex justify-between items-center p-2 rounded-lg bg-gray-50">
                                <div>
                                    <p className="font-semibold">{item.day} - <span className="text-gray-600">{item.time}</span></p>
                                    <p className="text-xs text-blue-600">{item.subjectName}</p>
                                </div>
                                <span className="font-mono text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-md">{item.className}</span>
                            </li>
                        )) : <p className="text-gray-500 text-center p-4">لا توجد حصص مجدولة.</p>}
                    </ul>
                </div>

                <div className="border-t pt-6 flex flex-col sm:flex-row gap-2">
                    <button onClick={() => showNotification("تم إرسال الرسالة!", "success")} className="flex-1 bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center justify-center">
                        <EnvelopeIcon className="w-5 h-5 ml-2" /> إرسال رسالة
                    </button>
                </div>
            </div>
        </div>
    );
};

const TeacherManagementPage = ({ teachers, classes, subjects, timetable, onAddTeacher, onUpdateTeacher, onDeleteTeacher, showNotification }) => {
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const [teacherSchedule, setTeacherSchedule] = useState([]);

    const classMap = new Map(classes.map(c => [c.id, c.name]));
    const subjectMap = new Map(subjects.map(s => [s.id, s.name]));

    const getTeacherSchedule = (teacherId) => {
        return timetable
            .filter(item => item.teacherId === teacherId)
            .map(item => ({
                ...item,
                className: classMap.get(item.classId) || 'غير محدد',
                subjectName: subjectMap.get(item.subjectId) || 'غير محدد'
            }));
    };

    const handleViewProfile = (teacher) => {
        const schedule = getTeacherSchedule(teacher.id);
        setTeacherSchedule(schedule);
        setSelectedTeacher(teacher);
    };

    const handleEditClick = (teacher) => {
        setSelectedTeacher(teacher);
        setShowEditModal(true);
    };

    const filteredTeachers = teachers.filter(teacher =>
        teacher.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        teacher.subject.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ show: false }))}
            />
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="relative w-full sm:w-auto sm:flex-1">
                    <input
                        type="text"
                        placeholder="بحث بالاسم أو المادة..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 border rounded-lg w-full pr-10 focus:ring-2 focus:ring-green-500 transition"
                    />
                    <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
                <button onClick={() => setShowAddModal(true)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center">
                    <UserPlusIcon className="w-5 h-5 ml-2" /> إضافة معلم
                </button>
            </div>

            <div className="relative">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTeachers.map(teacher => (
                        <div key={teacher.id} className="bg-white rounded-xl shadow-md p-5 flex flex-col text-center items-center card">
                            <img src={teacher.photoUrl} alt={teacher.name} className="w-24 h-24 rounded-full mb-4 border-4 border-green-200" />
                            <h4 className="text-xl font-bold text-gray-800">{teacher.name}</h4>
                            <p className="text-green-600 font-semibold">{teacher.subject}</p>
                            <div className="text-sm text-gray-500 my-4 border-t border-b w-full py-2">
                                <p>{teacher.email}</p>
                                <p>{teacher.phone}</p>
                            </div>
                            <div className="flex items-center justify-center space-x-2 rtl:space-x-reverse mt-auto">
                                <button onClick={() => handleViewProfile(teacher)} className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-100" title="عرض الملف الشخصي"><EyeIcon className="w-5 h-5" /></button>
                                <button onClick={() => handleEditClick(teacher)} className="text-yellow-500 hover:text-yellow-700 p-2 rounded-full hover:bg-yellow-100" title="تعديل"><PencilIcon className="w-5 h-5" /></button>
                                <button
                                    onClick={() => setConfirmModal({
                                        show: true,
                                        message: 'هل أنت متأكد أنك تريد حذف هذا المعلم؟ لا يمكن التراجع عن العملية.',
                                        onConfirm: () => {
                                            onDeleteTeacher(teacher.id);
                                            setConfirmModal({ show: false });
                                        },
                                        onCancel: () => setConfirmModal({ show: false })
                                    })}
                                    className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-100"
                                    title="حذف"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>

                {selectedTeacher && (
                    <TeacherDetailSidebar
                        teacher={selectedTeacher}
                        schedule={teacherSchedule}
                        onClose={() => setSelectedTeacher(null)}
                        showNotification={showNotification}
                    />
                )}
            </div>

            <Modal show={showAddModal} onClose={() => setShowAddModal(false)} title="إضافة معلم جديد">
                <AddTeacherForm onSave={onAddTeacher} onDone={() => setShowAddModal(false)} showNotification={showNotification} />
            </Modal>
            <Modal show={showEditModal} onClose={() => setShowEditModal(false)} title="تعديل بيانات المعلم">
                <AddTeacherForm onSave={onUpdateTeacher} onDone={() => setShowEditModal(false)} initialData={selectedTeacher} showNotification={showNotification} />
            </Modal>
        </div>
    );
};

const TimetableManagementPage = ({ timetable, classes, teachers, subjects, onAddTimetableEntry, onUpdateTimetableEntry, onDeleteTimetableEntry, showNotification }) => {
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [selectedClassId, setSelectedClassId] = useState(classes[0]?.id || '');
    const [showModal, setShowModal] = useState(false);
    const [editingSession, setEditingSession] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    const filteredClasses = classes.filter(cls => cls.name.toLowerCase().includes(searchTerm.toLowerCase()));

    useEffect(() => {
        if (!filteredClasses.find(c => c.id === selectedClassId)) {
            setSelectedClassId(filteredClasses[0]?.id || '');
        }
    }, [filteredClasses, selectedClassId]);

    const handleEditClick = (session) => {
        setEditingSession(session);
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingSession(null);
        setShowModal(true);
    };

    const handleSave = (sessionData) => {
        if (editingSession) {
            onUpdateTimetableEntry(sessionData);
        } else {
            onAddTimetableEntry({ ...sessionData, classId: selectedClassId });
        }
    };

    const teacherMap = new Map(teachers.map(t => [t.id, t]));
    const subjectMap = new Map(subjects.map(s => [s.id, s]));

    const days = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
    const times = ['08:00 - 09:00', '09:00 - 10:00', '10:00 - 11:00', '11:00 - 12:00', '13:00 - 14:00', '14:00 - 15:00', '15:00 - 16:00', '16:00 - 17:00', '17:00 - 18:00'];

    const classTimetable = timetable.filter(s => s.classId === selectedClassId);

    const handlePrint = () => {
        const printContent = document.getElementById('timetable-to-print');
        if (printContent) {
            const selectedClassName = classes.find(c => c.id === selectedClassId)?.name || '';
            const header = `<h2>الجدول الدراسي لقسم: ${selectedClassName}</h2>`;

            const printableHtml = `
                <html>
                <head>
                    <title>طباعة الجدول الدراسي</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
                        body { 
                            font-family: 'Cairo', sans-serif; 
                            direction: rtl;
                        }
                        table { 
                            width: 100%; 
                            border-collapse: collapse; 
                            font-size: 12px;
                        }
                        th, td { 
                            border: 1px solid #ddd; 
                            padding: 8px; 
                            text-align: center; 
                        }
                        th { 
                            background-color: #f2f2f2; 
                        }
                        h2 {
                            text-align: center;
                        }
                        .session-cell {
                            background-color: #f0f0ff !important;
                            -webkit-print-color-adjust: exact; 
                            print-color-adjust: exact;
                         }
                         .print-only {
                             display: block;
                         }
                         .no-print {
                             display: none;
                         }
                    </style>
                </head>
                <body>
                    ${header}
                    ${printContent.outerHTML}
                </body>
                </html>
            `;

            const printWindow = window.open('', '_blank');
            printWindow.document.write(printableHtml);
            printWindow.document.close();
            printWindow.focus();
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 250);
        }
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ show: false }))}
            />
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-4">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="ابحث عن قسم..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="p-2 border rounded-lg pr-10"
                        />
                        <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    </div>
                    <select id="class-select" value={selectedClassId} onChange={e => setSelectedClassId(e.target.value)} className="p-2 border rounded-lg bg-white focus:ring-2 focus:ring-purple-500">
                        {filteredClasses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handlePrint} className="bg-gray-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-700 transition flex items-center">
                        <PrintIcon className="w-5 h-5 ml-2" /> طباعة الجدول
                    </button>
                    <button onClick={handleAddNew} className="bg-purple-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-purple-700 transition flex items-center">
                        <ClockIcon className="w-5 h-5 ml-2" /> إضافة حصة
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                <table id="timetable-to-print" className="w-full text-center border-collapse min-w-[900px]">
                    <thead className="bg-purple-50">
                        <tr>
                            <th className="p-3 border">الوقت</th>
                            {days.map(day => <th key={day} className="p-3 border">{day}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {times.map(time => (
                            <tr key={time}>
                                <td className="p-2 border font-mono text-sm text-gray-600">{time}</td>
                                {days.map(day => {
                                    const session = classTimetable.find(s => s.day === day && s.time === time);
                                    return (
                                        <td key={day} className="p-2 border align-top h-28">
                                            {session ? (
                                                <div className="bg-purple-100 p-2 rounded-lg text-xs h-full flex flex-col justify-between session-cell">
                                                    <div>
                                                        <p className="font-bold text-purple-800">{subjectMap.get(session.subjectId)?.name}</p>
                                                        <p className="text-gray-600">{teacherMap.get(session.teacherId)?.name}</p>
                                                    </div>
                                                    <div className="flex justify-end mt-2 no-print">
                                                        <button onClick={() => handleEditClick(session)} className="text-yellow-600 p-1"><PencilIcon className="w-4 h-4" /></button>
                                                        <button
                                                            onClick={() => setConfirmModal({
                                                                show: true,
                                                                message: 'هل أنت متأكد أنك تريد حذف هذا الحصة من الجدول؟ لا يمكن التراجع عن العملية.',
                                                                onConfirm: () => {
                                                                    onDeleteTimetableEntry(session.id);
                                                                    setConfirmModal({ show: false });
                                                                },
                                                                onCancel: () => setConfirmModal({ show: false })
                                                            })}
                                                            className="text-red-600 p-1"
                                                            title="حذف"
                                                        >
                                                            <TrashIcon className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : null}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal show={showModal} onClose={() => setShowModal(false)} title={editingSession ? "تعديل الحصة" : "إضافة حصة جديدة"}>
                <AddEditSessionForm
                    onSave={handleSave}
                    onDone={() => setShowModal(false)}
                    initialData={editingSession}
                    classes={classes}
                    teachers={teachers}
                    subjects={subjects}
                    showNotification={showNotification}
                />
            </Modal>
        </div>
    );
};

const ClassManagementPage = ({ classes, students, enrollments, onAddClass, onUpdateClass, onDeleteClass, onAddEnrollment, onDeleteEnrollment, showNotification, fees, schoolSettings, onUpdateSchoolSettings }) => {
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [showModal, setShowModal] = useState(false);
    const [editingClass, setEditingClass] = useState(null);
    const [expandedLevel, setExpandedLevel] = useState(null);
    const [classDetails, setClassDetails] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('الكل');
    const [isReordering, setIsReordering] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);
    const [dragLevel, setDragLevel] = useState(null);
    const [orderedByLevel, setOrderedByLevel] = useState({});
    // Level ordering state
    const [levelOrder, setLevelOrder] = useState([]);
    const [isLevelReordering, setIsLevelReordering] = useState(false);
    const [levelDragIndex, setLevelDragIndex] = useState(null);
    const [levelDragOverIndex, setLevelDragOverIndex] = useState(null);

    const groupedClasses = classes.reduce((acc, cls) => {
        (acc[cls.level] = acc[cls.level] || []).push(cls);
        return acc;
    }, {});

    const derivedLevels = Object.keys(groupedClasses).sort();
    const levels = levelOrder && levelOrder.length > 0 ?
        // keep only existing levels, append any new ones
        [...levelOrder.filter(l => derivedLevels.includes(l)), ...derivedLevels.filter(l => !levelOrder.includes(l))]
        : derivedLevels;

    useEffect(() => {
        if (levels.length > 0 && !expandedLevel) {
            setExpandedLevel(levels[0]);
        }
    }, [levels, expandedLevel]);

    // Initialize level order from schoolSettings if present
    useEffect(() => {
        if (schoolSettings && Array.isArray(schoolSettings.levelOrder)) {
            setLevelOrder(schoolSettings.levelOrder);
        }
    }, [schoolSettings]);

    // When entering level reordering mode, ensure levelOrder is initialized from current derived levels
    useEffect(() => {
        if (isLevelReordering && (!levelOrder || levelOrder.length === 0)) {
            setLevelOrder(derivedLevels);
        }
    }, [isLevelReordering, derivedLevels]);

    const handleLevelDragStart = (index) => {
        setLevelDragIndex(index);
        setLevelDragOverIndex(index);
    };

    const handleLevelDragEnter = (index) => {
        if (levelDragIndex === null || levelDragIndex === index) return;
        setLevelDragOverIndex(index);
        setLevelOrder(prev => {
            const copy = [...prev];
            const item = copy.splice(levelDragIndex, 1)[0];
            copy.splice(index, 0, item);
            setLevelDragIndex(index);
            return copy;
        });
    };

    const handleLevelDragEnd = () => {
        setLevelDragIndex(null);
        setLevelDragOverIndex(null);
    };

    const saveLevelOrderToSettings = async () => {
        if (!onUpdateSchoolSettings) return;
        try {
            const newSettings = { ...(schoolSettings || {}), levelOrder };
            await onUpdateSchoolSettings(newSettings);
            showNotification('تم حفظ ترتيب المستويات.', 'success');
        } catch (err) {
            console.error('Failed to save level order to settings', err);
            showNotification('فشل حفظ ترتيب المستويات.', 'error');
        }
        setIsLevelReordering(false);
        setLevelDragIndex(null);
        setLevelDragOverIndex(null);
    };

    // Initialize ordered lists per level (respecting existing `order` field if present)
    useEffect(() => {
        const map = {};
        Object.keys(groupedClasses).forEach(levelKey => {
            const arr = [...(groupedClasses[levelKey] || [])];
            arr.sort((a, b) => (a.order || 0) - (b.order || 0) || a.name.localeCompare(b.name));
            map[levelKey] = arr;
        });
        setOrderedByLevel(map);
    }, [classes]);

    const handleDragStart = (levelKey, index) => {
        setDragIndex(index);
        setDragOverIndex(index);
        setDragLevel(levelKey);
    };

    const handleDragEnter = (levelKey, index) => {
        if (dragIndex === null || dragIndex === index) return;
        setDragOverIndex(index);
        // local reorder
        setOrderedByLevel(prev => {
            const copy = { ...prev };
            const list = [...(copy[levelKey] || [])];
            const item = list.splice(dragIndex, 1)[0];
            list.splice(index, 0, item);
            copy[levelKey] = list;
            setDragIndex(index);
            return copy;
        });
    };

    const handleDropSave = async (levelKey) => {
        const list = orderedByLevel[levelKey] || [];
        // persist new order to backend by setting `order` as index
        try {
            for (let i = 0; i < list.length; i++) {
                const cls = list[i];
                // only update if changed
                if ((cls.order || 0) !== i) {
                    await onUpdateClass({ ...cls, order: i });
                }
            }
            showNotification('تم حفظ ترتيب الفصول بنجاح.', 'success');
        } catch (err) {
            console.error('Error saving order:', err);
            showNotification('حدث خطأ أثناء حفظ الترتيب.', 'error');
        }
        setDragIndex(null);
        setDragOverIndex(null);
    };

    const handleSaveAllOrders = async () => {
        try {
            for (const levelKey of Object.keys(orderedByLevel)) {
                const list = orderedByLevel[levelKey] || [];
                for (let i = 0; i < list.length; i++) {
                    const cls = list[i];
                    if ((cls.order || 0) !== i) {
                        await onUpdateClass({ ...cls, order: i });
                    }
                }
            }
            showNotification('تم حفظ ترتيب كل الفصول.', 'success');
        } catch (err) {
            console.error('Error saving all orders:', err);
            showNotification('فشل حفظ الترتيب العام.', 'error');
        }
    };

    const handleDragEnd = () => {
        // persist the order for the level where the drag happened
        const levelToSave = dragLevel;
        setDragIndex(null);
        setDragOverIndex(null);
        setDragLevel(null);
        if (levelToSave) {
            // fire-and-forget; save errors are handled in handleDropSave
            handleDropSave(levelToSave);
        }
    };

    const handleEditClick = (cls) => {
        setEditingClass(cls);
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingClass(null);
        setShowModal(true);
    };

    const handleSave = (classData) => {
        if (editingClass) {
            onUpdateClass({ ...editingClass, ...classData });
        } else {
            onAddClass(classData);
        }
    };

    const getStudentCount = (classId) => enrollments.filter(e => e.classId === classId).length;

    const handleViewDetails = (cls) => {
        const enrolledStudents = enrollments
            .filter(e => e.classId === cls.id)
            .map(e => students.find(s => s.id === e.studentId))
            .filter(Boolean)
            .map(student => {
                const studentFees = fees.filter(f => f.studentId === student.id).sort((a, b) => b.createdAt - a.createdAt);
                return { ...student, feeStatus: studentFees[0]?.status || 'paid' };
            });

        const enrolledIds = new Set(enrolledStudents.map(s => s.id));
        const availableStudents = students.filter(s => !enrolledIds.has(s.id));

        setClassDetails({ ...cls, students: enrolledStudents, availableStudents });
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ show: false }))}
            />
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">إدارة الفصول الدراسية</h3>
                <button onClick={handleAddNew} className="bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition flex items-center">
                    <UserPlusIcon className="w-5 h-5 ml-2" /> إضافة فصل
                </button>
            </div>

            <div className="space-y-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCategoryFilter('الكل')} className={`px-3 py-1 rounded-md ${categoryFilter === 'الكل' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>الكل</button>
                        <button onClick={() => setCategoryFilter('أساسي')} className={`px-3 py-1 rounded-md ${categoryFilter === 'أساسي' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>أساسي</button>
                        <button onClick={() => setCategoryFilter('ثانوي')} className={`px-3 py-1 rounded-md ${categoryFilter === 'ثانوي' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>ثانوي</button>
                        <button onClick={() => setCategoryFilter('تكوين')} className={`px-3 py-1 rounded-md ${categoryFilter === 'تكوين' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}>تكوين</button>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsReordering(prev => !prev)} className={`px-3 py-1 rounded-md ${isReordering ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'}`}>{isReordering ? 'إنهاء الترتيب' : 'ترتيب الفصول'}</button>
                        {isReordering && <button onClick={handleSaveAllOrders} className="px-3 py-1 rounded-md bg-green-600 text-white">حفظ الكل</button>}
                    </div>
                </div>

                <div className="flex items-center gap-3 mb-2">
                    <button onClick={() => setIsLevelReordering(prev => !prev)} className={`px-3 py-1 rounded-md ${isLevelReordering ? 'bg-yellow-500 text-white' : 'bg-gray-200 text-gray-700'}`}>{isLevelReordering ? 'إنهاء ترتيب المستويات' : 'ترتيب المستويات'}</button>
                    {isLevelReordering && <button onClick={saveLevelOrderToSettings} className="px-3 py-1 rounded-md bg-green-600 text-white">حفظ ترتيب المستويات</button>}
                </div>

                {levels.map((level, levelIdx) => (
                    <div key={level} className="bg-white rounded-xl shadow-md overflow-hidden">
                        <button
                            draggable={isLevelReordering}
                            onDragStart={(e) => { if (isLevelReordering) { e.dataTransfer.effectAllowed = 'move'; handleLevelDragStart(levelIdx); } }}
                            onDragOver={(e) => { if (isLevelReordering) e.preventDefault(); }}
                            onDragEnter={() => isLevelReordering && handleLevelDragEnter(levelIdx)}
                            onDragEnd={() => isLevelReordering && handleLevelDragEnd()}
                            onClick={() => setExpandedLevel(expandedLevel === level ? null : level)}
                            className={`w-full text-right p-5 flex justify-between items-center ${isLevelReordering ? 'bg-yellow-50' : 'bg-indigo-50'} hover:bg-indigo-100`}
                        >
                            <h4 className="text-xl font-bold text-indigo-800">{level}</h4>
                            <ChevronDownIcon className={`w-6 h-6 text-indigo-600 transition-transform ${expandedLevel === level ? 'rotate-180' : ''}`} />
                        </button>

                        {expandedLevel === level && (
                            <div className="p-5 border-t border-indigo-100">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {(orderedByLevel[level] || groupedClasses[level] || []).filter(c => categoryFilter === 'الكل' ? true : (c.category || 'أساسي') === categoryFilter).map((cls, idx) => (
                                        <div
                                            key={cls.id}
                                            className={`bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md card ${isReordering ? 'cursor-grab' : ''} ${isReordering && dragOverIndex === idx ? 'ring-2 ring-offset-2 ring-indigo-200' : ''}`}
                                            draggable={isReordering}
                                            onDragStart={() => isReordering && handleDragStart(level, idx)}
                                            onDragEnter={() => isReordering && handleDragEnter(level, idx)}
                                            onDragEnd={() => isReordering && handleDragEnd()}
                                        >
                                            <div className="flex justify-between items-start gap-4">
                                                <div>
                                                    <h4 className="text-lg font-bold text-gray-800">{cls.name}</h4>
                                                    <p className="text-gray-500 text-sm mt-1">{cls.level}</p>
                                                </div>
                                                <div className="flex flex-col items-end gap-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${cls.category === 'ثانوي' ? 'bg-yellow-100 text-yellow-800' :
                                                                cls.category === 'تكوين' ? 'bg-purple-100 text-purple-800' :
                                                                    'bg-green-100 text-green-800'
                                                            }`}>{cls.category || 'أساسي'}</span>
                                                        <span className="bg-indigo-50 text-indigo-800 text-sm font-semibold px-3 py-1 rounded-full">{getStudentCount(cls.id)} طالب</span>
                                                    </div>
                                                    <div className="text-sm text-gray-400">قسم مرن</div>
                                                </div>
                                            </div>

                                            <div className="mt-4 border-t pt-4 flex justify-end space-x-2 rtl:space-x-reverse">
                                                <button onClick={() => handleViewDetails(cls)} className="text-blue-500 p-2 rounded-full hover:bg-blue-50" title="عرض التفاصيل"><EyeIcon className="w-5 h-5" /></button>
                                                <button onClick={() => handleEditClick(cls)} className="text-yellow-500 p-2 rounded-full hover:bg-yellow-50" title="تعديل"><PencilIcon className="w-5 h-5" /></button>
                                                <button
                                                    onClick={() => setConfirmModal({
                                                        show: true,
                                                        message: 'هل أنت متأكد أنك تريد حذف هذا الفصل؟ لا يمكن التراجع عن العملية.',
                                                        onConfirm: () => {
                                                            onDeleteClass(cls.id);
                                                            setConfirmModal({ show: false });
                                                        },
                                                        onCancel: () => setConfirmModal({ show: false })
                                                    })}
                                                    className="text-red-500 p-2 rounded-full hover:bg-red-50"
                                                    title="حذف"
                                                >
                                                    <TrashIcon className="w-5 h-5" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>


            <Modal show={showModal} onClose={() => setShowModal(false)} title={editingClass ? "تعديل الفصل" : "إضافة فصل جديد"}>
                <AddClassForm onSave={handleSave} onDone={() => setShowModal(false)} initialData={editingClass} showNotification={showNotification} />
            </Modal>

            {classDetails && (
                <Modal show={true} onClose={() => setClassDetails(null)} title={`تفاصيل قسم: ${classDetails.name}`} maxWidth="max-w-3xl">
                    <ClassDetailModal
                        classDetails={classDetails}
                        onAddEnrollment={onAddEnrollment}
                        onDeleteEnrollment={onDeleteEnrollment}
                        showNotification={showNotification}
                    />
                </Modal>
            )}
        </div>
    );
};

const ClassDetailModal = ({ classDetails, onAddEnrollment, onDeleteEnrollment, showNotification }) => {
    const [selectedStudent, setSelectedStudent] = useState('');

    const handleEnroll = () => {
        if (!selectedStudent) {
            showNotification("الرجاء اختيار طالب لإضافته.", "error");
            return;
        }
        onAddEnrollment({ studentId: selectedStudent, classId: classDetails.id });
        setSelectedStudent(''); // Reset dropdown
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'paid': return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">مدفوع</span>;
            case 'unpaid': return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">غير مدفوع</span>;
            case 'overdue': return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">متأخر</span>;
            default: return null;
        }
    };

    return (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto">
            <div>
                <h4 className="font-bold text-lg text-gray-700 mb-2">التلامذة المسجلون</h4>
                <ul className="space-y-2">
                    {classDetails.students.map(student => (
                        <li key={student.id} className="flex justify-between items-center bg-gray-100 p-2 rounded-lg">
                            <div className="flex items-center">
                                <img src={student.photoUrl} alt={student.firstName} className="w-8 h-8 rounded-full mr-3" />
                                <span>{student.firstName} {student.lastName}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                {getStatusChip(student.feeStatus)}
                                <button onClick={() => onDeleteEnrollment(student.id, classDetails.id)} className="text-red-500 p-1 hover:bg-red-100 rounded-full"><TrashIcon className="w-4 h-4" /></button>
                            </div>
                        </li>
                    ))}
                    {classDetails.students.length === 0 && <p className="text-gray-500">لا يوجد تلامذة مسجلون في هذا القسم بعد.</p>}
                </ul>
            </div>
            <div className="border-t pt-4">
                <h4 className="font-bold text-lg text-gray-700 mb-2">إضافة طالب جديد</h4>
                <div className="flex items-center gap-2">
                    <select value={selectedStudent} onChange={(e) => setSelectedStudent(e.target.value)} className="p-2 border rounded-lg w-full bg-white">
                        <option value="">-- اختر طالبًا --</option>
                        {classDetails.availableStudents.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                    </select>
                    <button onClick={handleEnroll} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition">إضافة</button>
                </div>
            </div>
        </div>
    );
};

const PaySalaryModal = ({ show, onClose, teachers, timetable, onPay, showNotification, teacherPayments }) => {
    const [selectedTeacherId, setSelectedTeacherId] = useState('');
    const [manualHours, setManualHours] = useState('');
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth()); // 0-indexed
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

    // Reset state when modal is closed or teacher changes
    useEffect(() => {
        if (!show) {
            setSelectedTeacherId('');
            setManualHours('');
            setSelectedMonth(new Date().getMonth());
            setSelectedYear(new Date().getFullYear());
        }
    }, [show]);

    const teacher = teachers.find(t => t.id === selectedTeacherId);

    const calculateHours = (teacherId) => {
        const sessions = timetable.filter(s => s.teacherId === teacherId);
        return sessions.length; // Assuming each session is 1 hour
    };

    let amountDue = 0;
    let calculationDetails = '';

    if (teacher) {
        if (teacher.paymentType === 'monthly') {
            amountDue = teacher.monthlyRate || 0;
            calculationDetails = 'راتب شهري ثابت';
        } else if (teacher.paymentType === 'hourly') {
            const workedHours = manualHours !== '' ? parseFloat(manualHours || 0) : calculateHours(teacher.id);
            const isManual = manualHours !== '';
            amountDue = (teacher.hourlyRate || 0) * workedHours;
            calculationDetails = `${workedHours} ${isManual ? 'ساعة (يدوي)' : 'حصة (آلي)'} × ${(teacher.hourlyRate || 0).toFixed(2)} د.ت = ${amountDue.toFixed(2)} د.ت`;
        }
    }

    const paymentForSelectedPeriod = teacher && teacherPayments.find(p => {
        return p.teacherId === teacher.id &&
            p.month === selectedMonth + 1 && // p.month is 1-12
            p.year === selectedYear;
    });

    const handlePay = () => {
        if (!teacher) {
            showNotification('الرجاء اختيار معلم', 'error');
            return;
        }
        if (amountDue <= 0) {
            showNotification('المبلغ للخلاص يجب أن يكون أكبر من صفر', 'error');
            return;
        }
        onPay(teacher.id, amountDue, calculationDetails, selectedMonth + 1, selectedYear);
        onClose();
    };

    const months = Array.from({ length: 12 }, (_, i) => new Date(0, i).toLocaleString('ar-TN', { month: 'long' }));
    const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i);

    return (
        <Modal show={show} onClose={onClose} title="دفع راتب لمعلم">
            <div className="space-y-4">
                <select value={selectedTeacherId} onChange={(e) => setSelectedTeacherId(e.target.value)} className="p-3 border rounded-lg w-full bg-white focus:ring-2 focus:ring-blue-500 transition" required>
                    <option value="">-- اختر معلم --</option>
                    {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>

                <div className="flex items-center gap-2">
                    <select
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                        className="p-2 border rounded-md w-full"
                    >
                        {months.map((month, index) => (
                            <option key={index} value={index}>{month}</option>
                        ))}
                    </select>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        className="p-2 border rounded-md w-full"
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>

                {teacher && (
                    <div className="p-4 bg-gray-50 rounded-lg space-y-3">
                        <h4 className="font-bold text-gray-800">{teacher.name}</h4>
                        <p>نوع الراتب: {teacher.paymentType === 'monthly' ? 'شهري' : 'بالساعة'}</p>
                        {teacher.paymentType === 'hourly' && (
                            <input
                                type="number"
                                placeholder="أدخل عدد الساعات (اختياري)"
                                value={manualHours}
                                onChange={(e) => setManualHours(e.target.value)}
                                className="p-2 border rounded-lg w-full"
                            />
                        )}
                        <p className="font-mono text-sm text-gray-600">{calculationDetails}</p>
                        <p className="text-xl font-bold">المبلغ للخلاص: {amountDue.toFixed(2)} د.ت</p>
                        {paymentForSelectedPeriod && (
                            <p className="text-green-600 font-semibold">تم دفع راتب هذا الشهر بالفعل.</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end pt-2">
                    <button onClick={handlePay} disabled={!teacher || amountDue <= 0 || !!paymentForSelectedPeriod} className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition disabled:bg-gray-400">
                        تأكيد الدفع
                    </button>
                </div>
            </div>
        </Modal>
    );
};

const TeacherSalaries = ({ teachers, showNotification, timetable, onPayTeacher, teacherPayments, db, appId }) => {
    const [manualHours, setManualHours] = useState({});
    const [showPayModal, setShowPayModal] = useState(false);

    const handleManualHoursChange = (teacherId, hours) => {
        setManualHours(prev => ({ ...prev, [teacherId]: hours }));
    };

    const handlePaySalary = (teacherId, amount, details, month, year) => {
        if (amount > 0) {
            // Ensure month is defined before passing to onPayTeacher
            const monthValue = month || new Date().getMonth() + 1; // Default to current month if undefined
            onPayTeacher({
                teacherId,
                amount,
                details,
                month: monthValue,
                year: year || new Date().getFullYear(), // Default to current year if undefined
            });
        } else {
            showNotification('لا يمكن دفع راتب بقيمة 0', 'error');
        }
    };

    const calculateHours = (teacherId) => {
        const sessions = timetable.filter(s => s.teacherId === teacherId);
        return sessions.length; // Assuming each session is 1 hour
    };

    const handlePrint = () => {
        const printContent = document.getElementById('salaries-to-print').innerHTML;
        const originalContent = document.body.innerHTML;
        document.body.innerHTML = `
            <html>
                <head>
                    <title>طباعة رواتب المعلمين</title>
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
                        body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 20px; }
                        h2 { text-align: center; margin-bottom: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 14px; }
                        th, td { border: 1px solid #ccc; padding: 8px; text-align: right; }
                        th { background: #f2f6ff; }
                        .no-print { display: none; }
                    </style>
                </head>
                <body>
                    <h2>كشف رواتب المعلمين</h2>
                    ${printContent}
                </body>
            </html>
        `;
        window.print();
        document.body.innerHTML = originalContent;
        window.location.reload();
    };

    const handlePrintSalaryInvoice = (teacher, amount, details, paymentDate) => {
        const printHtml = `
            <html>
            <head>
                <title>فاتورة راتب</title>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;700&display=swap');
                    body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 30px; }
                    .invoice-box { max-width: 800px; margin: auto; padding: 30px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0, 0, 0, .15); font-size: 16px; line-height: 24px; color: #555; }
                    h1 { color: #222; }
                </style>
            </head>
            <body>
                <div class="invoice-box">
                    <h1>فاتورة راتب</h1>
                    <p><strong>المعلم:</strong> ${teacher.name}</p>
                    <p><strong>التاريخ:</strong> ${paymentDate.toLocaleDateString('ar-TN')}</p>
                    <p><strong>المبلغ المدفوع:</strong> ${amount.toFixed(2)} د.ت</p>
                    <p><strong>التفاصيل:</strong> ${details}</p>
                </div>
            </body>
            </html>
        `;
        const win = window.open('', '_blank');
        win.document.write(printHtml);
        win.document.close();
        win.focus();
        setTimeout(() => {
            win.print();
            win.close();
        }, 250);
    };

    const handleDeleteTeacherPayment = async (paymentId) => {
        if (window.confirm('هل أنت متأكد من حذف هذه الدفعة؟')) {
            try {
                await deleteDoc(doc(db, `artifacts/${appId}/public/data/teacherPayments`, paymentId));
                showNotification('تم حذف دفعة المعلم بنجاح.', 'success');
            } catch (error) {
                console.error('Error deleting teacher payment:', error);
                showNotification('حدث خطأ أثناء حذف دفعة المعلم.', 'error');
            }
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md">
            <PaySalaryModal
                show={showPayModal}
                onClose={() => setShowPayModal(false)}
                teachers={teachers}
                timetable={timetable}
                onPay={handlePaySalary}
                showNotification={showNotification}
                teacherPayments={teacherPayments}
            />
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-2xl font-bold text-gray-700">إدارة رواتب المعلمين</h3>
                <button onClick={() => setShowPayModal(true)} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center">
                    <DollarSignIcon className="w-5 h-5 ml-2" /> دفع راتب لمعلم
                </button>
            </div>
            <div id="salaries-to-print" className="overflow-x-auto">
                <table className="w-full text-right">
                    <thead className="border-b-2">
                        <tr>
                            <th className="p-3">المعلم</th>
                            <th className="p-3">نوع الراتب</th>
                            <th className="p-3 no-print">الساعات المدخلة</th>
                            <th className="p-3">المبلغ للخلاص</th>
                            <th className="p-3">تفاصيل الحساب</th>
                            <th className="p-3">الحالة</th>
                            <th className="p-3 no-print">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {teachers.map(teacher => {
                            let amountDue = 0;
                            let hoursDetails = '-';
                            let calculationDetails = '';

                            if (teacher.paymentType === 'monthly') {
                                amountDue = teacher.monthlyRate || 0;
                                calculationDetails = 'راتب شهري ثابت';
                            } else if (teacher.paymentType === 'hourly') {
                                const workedHours = manualHours[teacher.id] !== undefined ? parseFloat(manualHours[teacher.id] || 0) : calculateHours(teacher.id);
                                const isManual = manualHours[teacher.id] !== undefined;

                                amountDue = (teacher.hourlyRate || 0) * workedHours;

                                hoursDetails = (
                                    <input
                                        type="number"
                                        placeholder="أدخل ساعات"
                                        value={manualHours[teacher.id] || ''}
                                        onChange={(e) => handleManualHoursChange(teacher.id, e.target.value)}
                                        className="p-2 border rounded-lg w-24 text-center no-print"
                                    />
                                );

                                calculationDetails = `${workedHours} ${isManual ? 'ساعة (يدوي)' : 'حصة (آلي)'} × ${(teacher.hourlyRate || 0).toFixed(2)} د.ت = ${amountDue.toFixed(2)} د.ت`;
                            }

                            const currentMonth = new Date().getMonth() + 1;
                            const currentYear = new Date().getFullYear();
                            const paymentThisMonth = teacherPayments.find(p => {
                                return p.teacherId === teacher.id &&
                                    p.month === currentMonth &&
                                    p.year === currentYear;
                            });
                            // Add null check for date property before calling toDate()
                            const paymentDate = paymentThisMonth && paymentThisMonth.date ? paymentThisMonth.date.toDate() : null;

                            return (
                                <tr key={teacher.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-semibold">{teacher.name}</td>
                                    <td className="p-3">{teacher.paymentType === 'monthly' ? 'شهري' : 'بالساعة'}</td>
                                    <td className="p-3 no-print">{hoursDetails}</td>
                                    <td className="p-3 font-mono">{amountDue.toFixed(2)} د.ت</td>
                                    <td className="p-3 font-mono text-xs text-gray-500">
                                        {calculationDetails}
                                    </td>
                                    <td className="p-3">
                                        {paymentDate ? (
                                            <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">
                                                مدفوع
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">
                                                غير مدفوع
                                            </span>
                                        )}
                                    </td>
                                    <td className="p-3 no-print">
                                        {paymentDate ? (
                                            <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-green-700">
                                                    {paymentDate.toLocaleDateString('ar-TN')}
                                                </span>
                                                <button
                                                    onClick={() => handlePrintSalaryInvoice(teacher, amountDue, calculationDetails, paymentDate)}
                                                    className="text-gray-500 hover:text-blue-600 p-1"
                                                    title="طباعة الفاتورة"
                                                >
                                                    <PrintIcon className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteTeacherPayment(paymentThisMonth.id)}
                                                    className="text-red-500 hover:text-red-600 p-1"
                                                    title="حذف الدفعة"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                    </svg>
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => handlePaySalary(teacher.id, amountDue, calculationDetails)}
                                                className="text-sm bg-blue-500 text-white py-1 px-3 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                                                disabled={amountDue === 0}
                                            >
                                                دفع
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TeacherPaymentRecords = ({ payments, teachers, onDeletePayment, showNotification }) => {
    const teacherMap = new Map(teachers.map(t => [t.id, t]));

    const handleDeletePayment = (paymentId) => {
        if (window.confirm('هل أنت متأكد من حذف هذه الدفعة؟')) {
            onDeletePayment(paymentId);
            showNotification('تم حذف دفعة المعلم بنجاح', 'success');
        }
    };

    const handlePrintPayment = (payment) => {
        const teacher = teacherMap.get(payment.teacherId);
        const printHtml = `
            <html>
            <head>
                <title>إيصال دفع</title>
                <style>
                    body { font-family: 'Cairo', sans-serif; direction: rtl; padding: 20px; }
                    .invoice-box { max-width: 600px; margin: auto; padding: 30px; border: 1px solid #eee; box-shadow: 0 0 10px rgba(0, 0, 0, .15); font-size: 16px; line-height: 24px; color: #555; }
                    h2 { text-align: center; margin-bottom: 20px; }
                    p { margin: 0 0 10px; }
                </style>
            </head>
            <body>
                <div class="invoice-box">
                    <h2>إيصال دفع راتب</h2>
                    <p><strong>المعلم:</strong> ${teacher ? teacher.name : 'غير معروف'}</p>
                    <p><strong>تاريخ الدفع:</strong> ${new Date(payment.date.toDate()).toLocaleDateString('ar-TN')}</p>
                    <p><strong>المبلغ:</strong> ${payment.amount.toFixed(2)} د.ت</p>
                    <p><strong>التفاصيل:</strong> ${payment.details}</p>
                </div>
            </body>
            </html>
        `;
        const win = window.open('', '_blank');
        win.document.write(printHtml);
        win.document.close();
        win.focus();
        setTimeout(() => {
            win.print();
            win.close();
        }, 250);
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="text-2xl font-bold text-gray-700 mb-4">سجل خلاص المعلمين</h3>
            <div className="overflow-x-auto">
                <table className="w-full text-right">
                    <thead className="border-b-2">
                        <tr>
                            <th className="p-3">المعلم</th>
                            <th className="p-3">تاريخ الخلاص</th>
                            <th className="p-3">المبلغ</th>
                            <th className="p-3">التفاصيل</th>
                            <th className="p-3">طباعة</th>
                        </tr>
                    </thead>
                    <tbody>
                        {payments.map(payment => {
                            const teacher = teacherMap.get(payment.teacherId);
                            return (
                                <tr key={payment.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-semibold">{teacher ? teacher.name : 'غير معروف'}</td>
                                    <td className="p-3 text-gray-600">{new Date(payment.date.toDate()).toLocaleDateString('ar-TN')}</td>
                                    <td className="p-3 font-mono">{payment.amount.toFixed(2)} د.ت</td>
                                    <td className="p-3 text-gray-600">{payment.details}</td>
                                    <td className="p-3">
                                        <div className="flex items-center gap-2">
                                            <button onClick={() => handlePrintPayment(payment)} className="text-gray-500 hover:text-blue-600 p-1" title="طباعة الإيصال">
                                                <PrintIcon className="w-5 h-5" />
                                            </button>
                                            <button
                                                onClick={() => handleDeletePayment(payment.id)}
                                                className="text-red-500 hover:text-red-700 p-1"
                                                title="حذف الدفعة">
                                                <TrashIcon className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )
                        })}
                        {payments.length === 0 && (
                            <tr>
                                <td colSpan="5" className="text-center p-4 text-gray-500">لا توجد سجلات خلاص حاليًا.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FinancialManagement = ({ fees, students, onAddFee, onUpdateFeeStatus, onDeleteFee, showNotification, enrollments, classes, teachers, timetable, subjects, teacherPayments, onPayTeacher, onDeleteTeacherPayment, db, appId }) => {
    const [activeTab, setActiveTab] = useState('dashboard');

    const TabButton = ({ tabName, label }) => (
        <button
            onClick={() => setActiveTab(tabName)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg ${activeTab === tabName ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
        >
            {label}
        </button>
    );

    return (
        <div className="p-4 md:p-8 space-y-8 fade-in-up">
            <div className="flex items-center space-x-2 rtl:space-x-reverse border-b pb-4">
                <TabButton tabName="dashboard" label="لوحة التحكم" />
                <TabButton tabName="records" label="سجل الأجور" />
                <TabButton tabName="salaries" label="خلاص المعلمين" />
                <TabButton tabName="teacher_records" label="سجل خلاص المعلمين" />
            </div>

            {activeTab === 'dashboard' && <FinancialDashboard fees={fees} students={students} enrollments={enrollments} classes={classes} />}
            {activeTab === 'records' && (
                <FeeRecords
                    fees={fees}
                    students={students}
                    classes={classes}
                    enrollments={enrollments}
                    onAddFee={onAddFee}
                    onUpdateFeeStatus={onUpdateFeeStatus}
                    onDeleteFee={onDeleteFee}
                    showNotification={showNotification}
                />
            )}
            {activeTab === 'salaries' && (
                <TeacherSalaries
                    teachers={teachers}
                    timetable={timetable}
                    subjects={subjects}
                    showNotification={showNotification}
                    onPayTeacher={onPayTeacher}
                    teacherPayments={teacherPayments}
                    db={db}
                    appId={appId}
                />
            )}
            {activeTab === 'teacher_records' && (
                <TeacherPaymentRecords
                    payments={teacherPayments}
                    teachers={teachers}
                    onDeletePayment={(paymentId) => {
                        if (window.confirm('هل أنت متأكد من حذف هذا السجل؟')) {
                            onDeleteTeacherPayment(paymentId);
                            showNotification('تم حذف سجل الخلاص بنجاح', 'success');
                        }
                    }}
                    showNotification={showNotification}
                />
            )}
        </div>
    );
};

const RevenueDistributionChart = ({ fees, students, enrollments, classes }) => {
    const revenueByLevel = {};

    const studentLevelMap = new Map();
    enrollments.forEach(e => {
        const studentClass = classes.find(c => c.id === e.classId);
        if (studentClass) {
            studentLevelMap.set(e.studentId, studentClass.level);
        }
    });

    let totalRevenue = 0;
    fees.forEach(fee => {
        if (fee.status === 'paid') {
            const level = studentLevelMap.get(fee.studentId);
            if (level) {
                if (!revenueByLevel[level]) {
                    revenueByLevel[level] = 0;
                }
                revenueByLevel[level] += fee.amount;
                totalRevenue += fee.amount;
            }
        }
    });

    if (totalRevenue === 0) {
        return (
            <div className="bg-white p-6 rounded-xl shadow-md">
                <h3 className="font-bold text-gray-700 mb-4 text-xl">توزيع الإيرادات حسب المستوى</h3>
                <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                    <ChartPieIcon className="w-12 h-12 text-gray-300 mb-2" />
                    <span>لا توجد إيرادات مسجلة لعرض المخطط.</span>
                </div>
            </div>
        )
    }

    const chartData = Object.entries(revenueByLevel).map(([level, amount]) => ({
        level,
        amount,
        percentage: (amount / totalRevenue) * 100,
    })).sort((a, b) => b.amount - a.amount);

    const colors = ['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#6366F1'];
    let startAngle = -90;

    const getArcPath = (percentage) => {
        const endAngle = startAngle + (percentage / 100) * 360;
        const startRad = (startAngle * Math.PI) / 180;
        const endRad = (endAngle * Math.PI) / 180;
        const x1 = 50 + 45 * Math.cos(startRad);
        const y1 = 50 + 45 * Math.sin(startRad);
        const x2 = 50 + 45 * Math.cos(endRad);
        const y2 = 50 + 45 * Math.sin(endRad);
        const largeArcFlag = percentage > 50 ? 1 : 0;
        const pathData = `M 50,50 L ${x1},${y1} A 45,45 0 ${largeArcFlag},1 ${x2},${y2} Z`;
        startAngle = endAngle;
        return pathData;
    };


    return (
        <div className="bg-white p-6 rounded-xl shadow-md">
            <h3 className="font-bold text-gray-700 mb-4 text-xl">توزيع الإيرادات حسب المستوى</h3>
            <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-8">
                <div className="w-full md:w-2/5 relative">
                    <svg viewBox="0 0 100 100" className="w-full h-auto drop-shadow-lg">
                        {chartData.map((item, index) => (
                            <path
                                key={item.level}
                                d={getArcPath(item.percentage)}
                                fill={colors[index % colors.length]}
                                className="pie-segment"
                                style={{ animationDelay: `${index * 100}ms` }}
                            >
                                <title>{item.level}: {item.amount.toFixed(2)} د.ت ({item.percentage.toFixed(1)}%)</title>
                            </path>
                        ))}
                        <circle cx="50" cy="50" r="30" fill="white" />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
                        <p className="text-gray-500 text-sm">الإجمالي</p>
                        <p className="text-2xl font-bold text-gray-800">
                            {totalRevenue.toLocaleString(APP_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-sm font-normal"> د.ت</span>
                        </p>
                    </div>
                </div>
                <div className="w-full md:w-3/5 space-y-3 text-sm">
                    {chartData.map((item, index) => (
                        <div key={item.level} className="flex justify-between items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors">
                            <div className="flex items-center gap-3">
                                <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: colors[index % colors.length] }}></div>
                                <span className="font-semibold text-gray-700">{item.level}</span>
                            </div>
                            <div className="text-gray-800 font-semibold">
                                <span>{item.amount.toLocaleString(APP_LOCALE, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} د.ت</span>
                                <span className="font-mono text-xs text-gray-500 font-normal ml-2">({item.percentage.toFixed(1)}%)</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
};


const FinancialDashboard = ({ fees, students, enrollments, classes }) => {
    const totalPaid = fees.filter(f => f.status === 'paid').reduce((sum, f) => sum + f.amount, 0);
    const totalUnpaid = fees.filter(f => f.status === 'unpaid').reduce((sum, f) => sum + f.amount, 0);
    const totalOverdue = fees.filter(f => f.status === 'overdue').reduce((sum, f) => sum + f.amount, 0);

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4 rtl:space-x-reverse">
                    <div className="p-3 bg-green-100 rounded-full"><ClipboardDocumentCheckIcon className="w-8 h-8 text-green-600" /></div>
                    <div>
                        <p className="text-gray-500">إجمالي المدفوعات</p>
                        <p className="text-2xl font-bold text-gray-800">{totalPaid.toFixed(2)} د.ت</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4 rtl:space-x-reverse">
                    <div className="p-3 bg-yellow-100 rounded-full"><ExclamationTriangleIcon className="w-8 h-8 text-yellow-600" /></div>
                    <div>
                        <p className="text-gray-500">الأجور غير المدفوعة</p>
                        <p className="text-2xl font-bold text-gray-800">{totalUnpaid.toFixed(2)} د.ت</p>
                    </div>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md flex items-center space-x-4 rtl:space-x-reverse">
                    <div className="p-3 bg-red-100 rounded-full"><ExclamationTriangleIcon className="w-8 h-8 text-red-600" /></div>
                    <div>
                        <p className="text-gray-500">المبالغ المتأخرة</p>
                        <p className="text-2xl font-bold text-gray-800">{totalOverdue.toFixed(2)} د.ت</p>
                    </div>
                </div>
            </div>
            <RevenueDistributionChart fees={fees} students={students} enrollments={enrollments} classes={classes} />
        </div>
    );
};

const FeeRecords = ({ fees, students, enrollments, classes, onAddFee, onUpdateFeeStatus, onDeleteFee, showNotification }) => {
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
    const [showFeeModal, setShowFeeModal] = useState(false);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedFee, setSelectedFee] = useState(null);
    const [filter, setFilter] = useState('all');
    const [selectedClass, setSelectedClass] = useState('');
    const [selectedLevel, setSelectedLevel] = useState('');

    const studentMap = new Map(students.map(s => [s.id, s]));
    const classMap = new Map(classes.map(c => [c.id, c]));
    const studentClassMap = new Map(enrollments.map(e => [e.studentId, e.classId]));

    const getStatusChip = (status) => {
        switch (status) {
            case 'paid': return <span className="px-2 py-1 text-xs font-semibold text-green-800 bg-green-200 rounded-full">مدفوع</span>;
            case 'unpaid': return <span className="px-2 py-1 text-xs font-semibold text-yellow-800 bg-yellow-200 rounded-full">غير مدفوع</span>;
            case 'overdue': return <span className="px-2 py-1 text-xs font-semibold text-red-800 bg-red-200 rounded-full">متأخر</span>;
            default: return null;
        }
    };

    const handleViewInvoice = (fee) => {
        setSelectedFee(fee);
        setShowInvoiceModal(true);
    };

    const filteredFees = fees.filter(fee => {
        // فلتر حالة الدفع
        const matchesStatus = filter === 'all' || fee.status === filter;

        // فلتر الفصل والمستوى
        const studentClassId = studentClassMap.get(fee.studentId);
        const studentClass = studentClassId ? classMap.get(studentClassId) : null;

        const matchesClass = !selectedClass || studentClassId === selectedClass;
        const matchesLevel = !selectedLevel || (studentClass && studentClass.level === selectedLevel);

        return matchesStatus && matchesClass && matchesLevel;
    });

    return (
        <div className="bg-white p-6 rounded-xl shadow-md">
            <ConfirmModal
                show={confirmModal.show}
                message={confirmModal.message}
                onConfirm={confirmModal.onConfirm}
                onCancel={confirmModal.onCancel || (() => setConfirmModal({ show: false }))}
            />
            {selectedFee && (
                <InvoiceModal
                    show={showInvoiceModal}
                    onClose={() => setShowInvoiceModal(false)}
                    fee={selectedFee}
                    student={studentMap.get(selectedFee.studentId)}
                />
            )}
            <Modal show={showFeeModal} onClose={() => setShowFeeModal(false)} title="إضافة رسم مالي جديد">
                <AddFeeForm students={students} onAdd={onAddFee} onDone={() => setShowFeeModal(false)} showNotification={showNotification} />
            </Modal>
            <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                <h3 className="text-2xl font-bold text-gray-700">سجل الأجور المالية</h3>
                <div className="flex flex-wrap items-center justify-center gap-4">
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        {/* فلتر المستوى */}
                        <select
                            value={selectedLevel}
                            onChange={(e) => {
                                setSelectedLevel(e.target.value);
                                setSelectedClass('');
                            }}
                            className="p-2 border rounded-lg text-sm"
                        >
                            <option value="">كل المستويات</option>
                            {[...new Set(classes.map(c => c.level))].sort().map(level => (
                                <option key={level} value={level}>{level}</option>
                            ))}
                        </select>

                        {/* فلتر الفصل */}
                        <select
                            value={selectedClass}
                            onChange={(e) => setSelectedClass(e.target.value)}
                            className="p-2 border rounded-lg text-sm"
                        >
                            <option value="">كل الفصول</option>
                            {classes
                                .filter(c => !selectedLevel || c.level === selectedLevel)
                                .map(cls => (
                                    <option key={cls.id} value={cls.id}>{cls.name}</option>
                                ))
                            }
                        </select>
                    </div>

                    <div className="flex items-center space-x-2 rtl:space-x-reverse bg-gray-100 rounded-lg p-1">
                        <button onClick={() => setFilter('all')} className={`px-3 py-1 text-sm rounded-md ${filter === 'all' ? 'bg-blue-600 text-white' : ''}`}>الكل</button>
                        <button onClick={() => setFilter('paid')} className={`px-3 py-1 text-sm rounded-md ${filter === 'paid' ? 'bg-blue-600 text-white' : ''}`}>مدفوع</button>
                        <button onClick={() => setFilter('unpaid')} className={`px-3 py-1 text-sm rounded-md ${filter === 'unpaid' ? 'bg-blue-600 text-white' : ''}`}>غير مدفوع</button>
                        <button onClick={() => setFilter('overdue')} className={`px-3 py-1 text-sm rounded-md ${filter === 'overdue' ? 'bg-blue-600 text-white' : ''}`}>متأخر</button>
                    </div>
                    <button onClick={() => setShowFeeModal(true)} className="bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700 transition flex items-center">
                        إضافة رسم
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-right">
                    <thead className="border-b-2">
                        <tr>
                            <th className="p-3">التلميذ</th>
                            <th className="p-3">الوصف</th>
                            <th className="p-3">المبلغ</th>
                            <th className="p-3">تاريخ الخلاص</th>
                            <th className="p-3">الحالة</th>
                            <th className="p-3">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredFees.map(fee => {
                            const student = studentMap.get(fee.studentId);
                            return (
                                <tr key={fee.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-semibold">{student ? `${student.firstName} ${student.lastName}` : 'غير معروف'}</td>
                                    <td className="p-3 text-gray-600">{fee.description}</td>
                                    <td className="p-3 font-mono">{fee.amount.toFixed(2)} د.ت</td>
                                    <td className="p-3 text-gray-600">{new Date(fee.dueDate).toLocaleDateString(APP_LOCALE)}</td>
                                    <td className="p-3">{getStatusChip(fee.status)}</td>
                                    <td className="p-3">
                                        <select
                                            value={fee.status}
                                            onChange={(e) => onUpdateFeeStatus(fee.id, e.target.value)}
                                            className="text-xs p-1 border rounded bg-white"
                                        >
                                            <option value="unpaid">غير مدفوع</option>
                                            <option value="paid">مدفوع</option>
                                            <option value="overdue">متأخر</option>
                                        </select>
                                        <button onClick={() => handleViewInvoice(fee)} className="text-blue-500 hover:text-blue-700 p-1 mx-1" title="عرض الفاتورة"><EyeIcon className="w-5 h-5" /></button>
                                        <button
                                            onClick={() => setConfirmModal({
                                                show: true,
                                                message: 'هل أنت متأكد أنك تريد حذف هذه الأجور؟ لا يمكن التراجع عن العملية.',
                                                onConfirm: () => {
                                                    onDeleteFee(fee.id);
                                                    setConfirmModal({ show: false });
                                                },
                                                onCancel: () => setConfirmModal({ show: false })
                                            })}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="حذف"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const SystemSettings = ({ settings, onSave, showNotification, classes }) => {
    const [formData, setFormData] = useState({
        ...settings,
        feeGenerationDay: settings?.feeGenerationDay || 1,
        feesPerLevel: settings?.feesPerLevel || {}
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        setFormData({
            ...settings,
            feeGenerationDay: settings?.feeGenerationDay || 1,
            feesPerLevel: settings?.feesPerLevel || {}
        });
    }, [settings]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // تخزين الصورة كـ Base64 مؤقتًا حتى يتم تحميلها للسيرفر عند الحفظ
                setFormData(prev => ({ ...prev, schoolLogo: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        // منع السلوك الافتراضي للنموذج لمنع إعادة تحميل الصفحة
        if (e) e.preventDefault();

        // Validate academic year dates if present
        try {
            if (formData.academicYearStart && formData.academicYearEnd) {
                const start = new Date(formData.academicYearStart);
                const end = new Date(formData.academicYearEnd);
                start.setHours(0, 0, 0, 0);
                end.setHours(0, 0, 0, 0);
                if (start > end) {
                    showNotification('تاريخ بداية العام لا يمكن أن يكون بعد تاريخ نهاية العام.', 'error');
                    return;
                }
            }
        } catch (err) {
            // ignore parsing errors, allow server-side validation
        }

        try {
            setIsSubmitting(true);
            await onSave(formData);
            showNotification('تم حفظ الإعدادات بنجاح', 'success');
        } catch (error) {
            console.error('خطأ في حفظ الإعدادات:', error);
            showNotification('حدث خطأ أثناء حفظ الإعدادات', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-800">إعدادات النظام</h3>
            <div className="space-y-8">
                {/* School Info */}
                <div className="p-6 border rounded-xl bg-gray-50">
                    <h4 className="font-bold text-lg mb-4 text-gray-700">معلومات المدرسة</h4>
                    <div className="space-y-4">
                        <div className="flex items-center gap-6">
                            <img src={formData.schoolLogo} alt="شعار المدرسة" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" />
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-600 mb-1">شعار المدرسة</label>
                                <input type="file" accept="image/*" onChange={handleImageChange} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                            </div>
                        </div>
                        <div>
                            <label htmlFor="schoolName" className="block text-sm font-medium text-gray-600 mb-1">اسم المدرسة</label>
                            <input type="text" id="schoolName" name="schoolName" value={formData.schoolName} onChange={handleChange} className="p-2 border rounded-lg w-full" />
                        </div>
                        <div>
                            <label htmlFor="language" className="block text-sm font-medium text-gray-600 mb-1">اللغة الافتراضية</label>
                            <select id="language" name="language" value={formData.language || 'ar'} onChange={handleChange} className="p-2 border rounded-lg w-full">
                                <option value="ar">العربية</option>
                                <option value="fr">Français</option>
                                <option value="en">English</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Fee Settings */}
                <FeeSettings formData={formData} handleChange={handleChange} classes={classes} />

                {/* Academic Year */}
                <div className="p-6 border rounded-xl bg-gray-50">
                    <h4 className="font-bold text-lg mb-4 text-gray-700">العام الدراسي</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="academicYearStart" className="block text-sm font-medium text-gray-600 mb-1">تاريخ بداية العام</label>
                            <input type="date" id="academicYearStart" name="academicYearStart" value={formData.academicYearStart} onChange={handleChange} className="p-2 border rounded-lg w-full" />
                        </div>
                        <div>
                            <label htmlFor="academicYearEnd" className="block text-sm font-medium text-gray-600 mb-1">تاريخ نهاية العام</label>
                            <input type="date" id="academicYearEnd" name="academicYearEnd" value={formData.academicYearEnd} onChange={handleChange} className="p-2 border rounded-lg w-full" />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-4 pt-4">
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            try {
                                handleSubmit();
                            } catch (error) {
                                console.error("Error saving changes:", error);
                            }
                            return false;
                        }}
                        className={`bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center w-32 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {isSubmitting ? <Spinner /> : t('labels.saveChanges', 'حفظ التغييرات')}
                    </a>

                    {/* تم حذف زر "حفظ الإعدادات" الأخضر */}
                </div>
            </div>
        </div>
    );
};

const AddEditUserForm = ({ onSave, onDone, initialData, showNotification, students }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'إداري',
        studentId: '', // For parent accounts
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const isEditing = !!initialData;

    useEffect(() => {
        if (initialData) {
            setFormData({
                id: initialData.id,
                name: initialData.name || '',
                email: initialData.email || '',
                password: '',
                role: initialData.role || 'إداري',
                studentId: initialData.studentId || '',
            });
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email || !formData.name) {
            showNotification('الرجاء ملء الاسم والبريد الإلكتروني.', 'error');
            return;
        }
        if (!isEditing && !formData.password) {
            showNotification('الرجاء إدخال كلمة مرور للمستخدم الجديد.', 'error');
            return;
        }
        if (formData.role === 'ولي_أمر' && !formData.studentId) {
            showNotification('الرجاء اختيار الطالب المرتبط بحساب ولي الأمر.', 'error');
            return;
        }
        setIsSubmitting(true);
        await onSave(formData);
        setIsSubmitting(false);
        onDone();
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto p-1">
            <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="الاسم الكامل"
                className="p-3 border rounded-lg w-full"
                required
            />
            <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="البريد الإلكتروني"
                className="p-3 border rounded-lg w-full"
                required
                disabled={isEditing}
            />
            {!isEditing && (
                <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="كلمة المرور"
                    className="p-3 border rounded-lg w-full"
                    required
                />
            )}
            <div className="space-y-1">
                <label htmlFor="role" className="block text-sm text-gray-600">
                    الدور الوظيفي
                </label>
                <select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="p-3 border rounded-lg w-full bg-white"
                    required
                >
                    <option value="مدير">مدير</option>
                    <option value="إداري">إداري</option>
                    <option value="معلم">معلم</option>
                    <option value="ولي_أمر">ولي أمر</option>
                </select>
            </div>

            {formData.role === 'ولي_أمر' && (
                <div className="space-y-1">
                    <label htmlFor="studentId" className="block text-sm text-gray-600">
                        الطالب
                    </label>
                    <select
                        id="studentId"
                        name="studentId"
                        value={formData.studentId}
                        onChange={handleChange}
                        className="p-3 border rounded-lg w-full bg-white"
                        required
                    >
                        <option value="">-- اختر الطالب --</option>
                        {students?.map(student => (
                            <option key={student.id} value={student.id}>
                                {student.firstName} {student.lastName}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            <div className="flex justify-end pt-2">
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-blue-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center w-32 disabled:bg-blue-300"
                >
                    {isSubmitting ? <Spinner /> : t('labels.save', 'حفظ')}
                </button>
            </div>
        </form>
    );
};

const UserManagement = ({ users, onAddUser, onUpdateUser, onDeleteUser, showNotification, auth, students }) => {
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const handleAddNew = () => {
        setEditingUser(null);
        setShowUserModal(true);
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setShowUserModal(true);
    };

    const handleSaveUser = (userData) => {
        if (editingUser) {
            onUpdateUser(userData);
        } else {
            onAddUser(userData);
        }
    };

    const handleResetPassword = (email) => {
        sendPasswordResetEmail(auth, email)
            .then(() => {
                showNotification(`تم إرسال رابط إعادة تعيين كلمة المرور إلى ${email}`, 'success');
            })
            .catch((error) => {
                showNotification('حدث خطأ أثناء إرسال البريد الإلكتروني.', 'error');
                console.error("Password Reset Error:", error);
            });
    };

    return (
        <div>
            <h3 className="text-2xl font-bold mb-6 text-gray-800">إدارة المستخدمين والصلاحيات</h3>
            <div className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex justify-end mb-4">
                    <button onClick={handleAddNew} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition flex items-center">
                        <UserPlusIcon className="w-5 h-5 ml-2" /> إضافة مستخدم
                    </button>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-right min-w-[600px]">
                        <thead className="border-b-2">
                            <tr>
                                <th className="p-3">الاسم</th>
                                <th className="p-3">البريد الإلكتروني</th>
                                <th className="p-3">الدور</th>
                                <th className="p-3">إجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-semibold">{user.name}</td>
                                    <td className="p-3 text-gray-600">{user.email}</td>
                                    <td className="p-3">
                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${user.role === 'ولي_أمر'
                                                ? 'bg-orange-100 text-orange-800'
                                                : 'bg-green-100 text-green-800'
                                            }`}>
                                            {user.role === 'ولي_أمر' ? 'ولي أمر' : user.role}
                                            {user.studentId && user.role === 'ولي_أمر' && (
                                                <span className="text-gray-500 text-xs block">
                                                    {students?.find(s => s.id === user.studentId)?.firstName}
                                                </span>
                                            )}
                                        </span>
                                    </td>
                                    <td className="p-3 flex items-center space-x-2 rtl:space-x-reverse">
                                        <button onClick={() => handleEdit(user)} className="text-yellow-500 hover:text-yellow-700 p-1" title="تعديل الصلاحيات"><PencilIcon className="w-5 h-5" /></button>
                                        <button onClick={() => handleResetPassword(user.email)} className="text-blue-500 hover:text-blue-700 p-1" title="إعادة تعيين كلمة المرور"><KeyIcon className="w-5 h-5" /></button>
                                        <button onClick={() => onDeleteUser(user.id)} className="text-red-500 hover:text-red-700 p-1" title="حذف"><TrashIcon className="w-5 h-5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal show={showUserModal} onClose={() => setShowUserModal(false)} title={editingUser ? 'تعديل المستخدم' : 'إضافة مستخدم جديد'}>
                <AddEditUserForm
                    initialData={editingUser}
                    onSave={handleSaveUser}
                    onDone={() => setShowUserModal(false)}
                    showNotification={showNotification}
                    students={students}
                />
            </Modal>
        </div>
    )
}

const SettingsPage = ({ schoolSettings, onUpdateSchoolSettings, showNotification, users, onAddUser, onUpdateUser, onDeleteUser, auth, classes, students }) => {
    const [activeTab, setActiveTab] = useState('system');

    const TabButton = ({ tabName, icon, label }) => (
        <button
            onClick={() => setActiveTab(tabName)}
            className={`flex items-center space-x-3 rtl:space-x-reverse w-full text-right px-4 py-3 rounded-lg transition ${activeTab === tabName ? 'bg-blue-600 text-white' : 'hover:bg-gray-100 text-gray-700'}`}
        >
            {icon}
            <span>{label}</span>
        </button>
    );

    const renderContent = () => {
        switch (activeTab) {
            case 'system': return <SystemSettings key={JSON.stringify(schoolSettings)} settings={schoolSettings} classes={classes || []} onSave={onUpdateSchoolSettings} showNotification={showNotification} />;
            case 'users': return <UserManagement users={users} onAddUser={onAddUser} onUpdateUser={onUpdateUser} onDeleteUser={onDeleteUser} showNotification={showNotification} auth={auth} students={students} />;
            default: return null;
        }
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <div className="bg-white p-4 rounded-xl shadow-md flex flex-col md:flex-row gap-6">
                <aside className="w-full md:w-1/4 lg:w-1/5">
                    <nav className="space-y-2">
                        <TabButton tabName="system" label="إعدادات النظام" icon={<Cog6ToothIcon className="w-5 h-5" />} />
                        <TabButton tabName="users" label="المستخدمين والصلاحيات" icon={<UsersGroupIcon className="w-5 h-5" />} />
                    </nav>
                </aside>
                <main className="flex-1 p-6 border-r border-gray-200">
                    {renderContent()}
                </main>
            </div>
        </div>
    );
};

const SubjectManagementPage = ({ subjects, onAddSubject, onUpdateSubject, onDeleteSubject, showNotification, timetable }) => {
    const [showModal, setShowModal] = useState(false);
    const [editingSubject, setEditingSubject] = useState(null);

    const handleEditClick = (subject) => {
        setEditingSubject(subject);
        setShowModal(true);
    };

    const handleAddNew = () => {
        setEditingSubject(null);
        setShowModal(true);
    };

    const handleSave = (subjectData) => {
        if (editingSubject) {
            onUpdateSubject(subjectData);
        } else {
            onAddSubject(subjectData);
        }
    };

    const isSubjectInUse = (subjectId) => {
        return timetable.some(session => session.subjectId === subjectId);
    };

    const handleDeleteClick = (subjectId) => {
        if (isSubjectInUse(subjectId)) {
            showNotification('لا يمكن حذف المادة لأنها مستخدمة في الجدول الدراسي.', 'error');
        } else {
            onDeleteSubject(subjectId);
        }
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex justify-between items-center">
                <h3 className="text-2xl font-bold text-gray-800">إدارة المواد الدراسية</h3>
                <button onClick={handleAddNew} className="bg-amber-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-amber-700 transition flex items-center">
                    <UserPlusIcon className="w-5 h-5 ml-2" /> إضافة مادة
                </button>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-md">
                <div className="overflow-x-auto">
                    <table className="w-full text-right">
                        <thead className="border-b-2">
                            <tr>
                                <th className="p-3">اسم المادة</th>
                                <th className="p-3 text-center">الإجراءات</th>
                            </tr>
                        </thead>
                        <tbody>
                            {subjects.map(subject => (
                                <tr key={subject.id} className="border-b hover:bg-gray-50">
                                    <td className="p-3 font-semibold">{subject.name}</td>
                                    <td className="p-3 text-center">
                                        <button onClick={() => handleEditClick(subject)} className="text-yellow-500 hover:text-yellow-700 p-1 mx-1" title="تعديل"><PencilIcon className="w-5 h-5" /></button>
                                        <button onClick={() => handleDeleteClick(subject.id)} className="text-red-500 hover:text-red-700 p-1" title="حذف"><TrashIcon className="w-5 h-5" /></button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <Modal show={showModal} onClose={() => setShowModal(false)} title={editingSubject ? "تعديل المادة" : "إضافة مادة جديدة"}>
                <AddEditSubjectForm onSave={handleSave} onDone={() => setShowModal(false)} initialData={editingSubject} showNotification={showNotification} />
            </Modal>
        </div>
    );
};




const EditParentModal = ({ show, onClose, parent, allStudents, onSave, showNotification }) => {
    const [formData, setFormData] = useState({
        name: parent.name,
        phone: parent.phone,
        email: parent.email || '',
    });
    const [selectedChildren, setSelectedChildren] = useState(parent.children.map(c => c.id));
    const [isSubmitting, setIsSubmitting] = useState(false);

    const availableStudents = allStudents.filter(student =>
        !selectedChildren.includes(student.id) &&
        (student.parentPhone === parent.phone || !student.parentPhone)
    );

    useEffect(() => {
        setFormData({
            name: parent.name,
            phone: parent.phone,
            email: parent.email || '',
        });
        setSelectedChildren(parent.children.map(c => c.id));
    }, [parent]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleChildSelect = (e) => {
        const studentId = e.target.value;
        if (studentId && !selectedChildren.includes(studentId)) {
            setSelectedChildren(prev => [...prev, studentId]);
        }
    };

    const handleChildRemove = (studentId) => {
        setSelectedChildren(prev => prev.filter(id => id !== studentId));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        await onSave(parent.id, formData, selectedChildren);
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Modal show={show} onClose={onClose} title="تعديل بيانات ولي الأمر" maxWidth="max-w-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">اسم ولي الأمر</label>
                    <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="mt-1 p-2 border rounded-md w-full"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">رقم الهاتف</label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="mt-1 p-2 border rounded-md w-full"
                        required
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">البريد الإلكتروني</label>
                    <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="mt-1 p-2 border rounded-md w-full"
                    />
                </div>

                <div className="border-t pt-4">
                    <h3 className="text-lg font-bold text-gray-800 mb-3">الأبناء المرتبطون</h3>
                    <div className="space-y-2">
                        {selectedChildren.map(childId => {
                            const child = allStudents.find(s => s.id === childId);
                            return child ? (
                                <div key={child.id} className="flex justify-between items-center bg-gray-100 p-2 rounded-md">
                                    <span>{child.firstName} {child.lastName}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleChildRemove(child.id)}
                                        className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-100"
                                    >
                                        <XMarkIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            ) : null;
                        })}
                    </div>
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700">إضافة ابن/ابنة</label>
                        <select
                            onChange={handleChildSelect}
                            value=""
                            className="mt-1 p-2 border rounded-md w-full bg-white"
                        >
                            <option value="">-- اختر طالباً لإضافته --</option>
                            {availableStudents.map(student => (
                                <option key={student.id} value={student.id}>
                                    {student.firstName} {student.lastName}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex justify-end pt-4">
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:bg-blue-300"
                    >
                        {isSubmitting ? <Spinner /> : 'حفظ التغييرات'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

const ParentManagementPage = ({ students, showNotification, t, onUpdateParent }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showEditParentModal, setShowEditParentModal] = useState(false);
    const [selectedParent, setSelectedParent] = useState(null);

    const parents = useMemo(() => {
        const parentMap = new Map();
        students.forEach(student => {
            // Use parentPhone as the unique key for a parent
            const parentId = student.parentPhone;
            if (parentId) {
                if (!parentMap.has(parentId)) {
                    parentMap.set(parentId, {
                        id: parentId,
                        phone: student.parentPhone,
                        name: student.fatherName || student.motherName || 'غير متوفر',
                        email: student.email,
                        children: [],
                    });
                }
                const parent = parentMap.get(parentId);
                parent.children.push({
                    id: student.id,
                    name: `${student.firstName} ${student.lastName}`
                });
                // Update parent info if current student has more details
                if (parent.name === 'غير متوفر' && (student.fatherName || student.motherName)) {
                    parent.name = student.fatherName || student.motherName;
                }
                if (!parent.email && student.email) {
                    parent.email = student.email;
                }
            }
        });
        return Array.from(parentMap.values());
    }, [students]);

    const filteredParents = useMemo(() => {
        if (!searchTerm) return parents;
        const lowercasedSearchTerm = searchTerm.toLowerCase();
        return parents.filter(parent =>
            (parent.name && parent.name.toLowerCase().includes(lowercasedSearchTerm)) ||
            (parent.phone && parent.phone.includes(lowercasedSearchTerm)) ||
            (parent.email && parent.email.toLowerCase().includes(lowercasedSearchTerm))
        );
    }, [parents, searchTerm]);

    const handleEditParent = (parent) => {
        setSelectedParent(parent);
        setShowEditParentModal(true);
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 min-h-full">
            <div className="bg-white p-4 rounded-xl shadow-md mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                <h2 className="text-2xl font-bold text-gray-800">{t('pageTitles.parentManagement')}</h2>
                <div className="relative w-full sm:w-auto sm:flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder="بحث بالاسم، الهاتف، أو البريد الإلكتروني..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="p-2 border rounded-lg w-full pr-10 focus:ring-2 focus:ring-blue-500 transition"
                    />
                    <SearchIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                </div>
            </div>

            <div className="bg-white p-4 rounded-xl shadow-md overflow-x-auto">
                <table className="w-full text-right min-w-[800px]">
                    <thead className="border-b-2 text-gray-600">
                        <tr>
                            <th className="p-3">اسم ولي الأمر</th>
                            <th className="p-3">الهاتف</th>
                            <th className="p-3">البريد الإلكتروني</th>
                            <th className="p-3">الأبناء المسجلون</th>
                            <th className="p-3">إجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredParents.map(parent => (
                            <tr key={parent.id} className="border-b hover:bg-blue-50">
                                <td className="p-3 font-semibold">{parent.name}</td>
                                <td className="p-3 text-gray-600 font-mono">{parent.phone}</td>
                                <td className="p-3 text-gray-600">{parent.email || '-'}</td >
                                <td className="p-3 text-gray-600 text-sm">
                                    {parent.children.map(child => child.name).join(', ')}
                                </td>
                                <td className="p-3">
                                    <button onClick={() => handleEditParent(parent)} className="text-blue-500 hover:underline">
                                        تعديل
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {selectedParent && (
                <EditParentModal
                    show={showEditParentModal}
                    onClose={() => setShowEditParentModal(false)}
                    parent={selectedParent}
                    allStudents={students}
                    onSave={onUpdateParent}
                    showNotification={showNotification}
                />
            )}
        </div>
    );
};

// --- Landing Page Component ---
const LandingPage = ({ onNavigateToLogin, onNavigateToParentLogin, schoolSettings }) => {
    return (
        <div
            className="min-h-screen flex flex-col items-center justify-center p-6 text-center text-white bg-cover bg-center"
            style={{ backgroundImage: "linear-gradient(rgba(29, 78, 216, 0.85), rgba(30, 64, 175, 0.85)), url('https://images.unsplash.com/photo-1509062522246-3755977927d7?q=80&w=2132&auto=format&fit=crop')" }}
            dir="rtl"
        >
            <div className="max-w-4xl w-full">
                <div className="fade-in-up">
                    <img
                        src={schoolSettings.schoolLogo || 'https://pythagoreschool.com/logo192.png'}
                        alt="Pythagore School"
                        className="w-24 h-24 md:w-32 md:h-32 rounded-full mx-auto mb-6 border-4 border-white/50 shadow-lg"
                    />
                    <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                        مرحباً بكم في {schoolSettings.schoolName || 'مدرستنا'}
                    </h1>
                    <div className="max-w-2xl mx-auto bg-black/20 backdrop-blur-sm rounded-xl p-4 my-8">
                        <h2 className="font-bold text-xl mb-2 border-b border-white/30 pb-2">نبذة عن المدرسة</h2>
                        <p className="text-white/90 mt-2">
                            مؤسستنا التعليمية ملتزمة بتوفير بيئة تعليمية محفزة ورعاية شاملة تساعد على نمو التلامذة الأكاديمي والشخصي. نحن نؤمن بأهمية بناء جيل واعٍ ومثقف.
                        </p>
                    </div>
                    <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={onNavigateToLogin}
                            className="bg-white text-blue-700 font-bold text-xl py-3 px-12 rounded-full shadow-xl transform hover:scale-105 transition-transform duration-300 ease-in-out"
                        >
                            دخول الإدارة
                        </button>
                        <button
                            onClick={onNavigateToParentLogin}
                            className="bg-blue-700 text-white font-bold text-xl py-3 px-12 rounded-full shadow-xl transform hover:scale-105 transition-transform duration-300 ease-in-out border-2 border-white/30"
                        >
                            بوابة أولياء الأمور
                        </button>
                    </div>
                    <div className="mt-6 text-sm text-white/70">
                        هل أنت ولي أمر؟ قم بالدخول عبر بوابة أولياء الأمور
                    </div>
                </div>
            </div>
        </div>
    );
};


// --- Login Page Component ---
const LoginPage = ({ auth, showNotification, schoolSettings, onBackToLanding }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !password) {
            showNotification('الرجاء إدخال البريد الإلكتروني وكلمة المرور.', 'error');
            return;
        }
        setIsLoading(true);
        try {
            await signInWithEmailAndPassword(auth, email, password);
            // onAuthStateChanged will handle redirecting to the dashboard
        } catch (error) {
            console.error("Login Error:", error.code);
            showNotification('البريد الإلكتروني أو كلمة المرور غير صحيحة.', 'error');
        }
        setIsLoading(false);
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4" dir="rtl">
            <div className="w-full max-w-md">
                <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
                    <img src={schoolSettings.schoolLogo} alt="شعار المدرسة" className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-blue-200" />
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">{schoolSettings.schoolName}</h1>
                    <p className="text-gray-500 mb-6">مرحباً بعودتك! الرجاء تسجيل الدخول للمتابعة.</p>
                    <form onSubmit={handleLogin} className="space-y-4">
                        <input
                            type="email"
                            placeholder="البريد الإلكتروني"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="p-3 border rounded-lg w-full text-right focus:ring-2 focus:ring-blue-500 transition"
                            required
                        />
                        <input
                            type="password"
                            placeholder="كلمة المرور"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="p-3 border rounded-lg w-full text-right focus:ring-2 focus:ring-blue-500 transition"
                            required
                        />
                        <button type="submit" disabled={isLoading} className="w-full bg-blue-600 text-white font-bold py-3 px-6 rounded-lg hover:bg-blue-700 transition flex items-center justify-center disabled:bg-blue-300">
                            {isLoading ? <Spinner /> : 'تسجيل الدخول'}
                        </button>
                    </form>
                    <button onClick={onBackToLanding} className="mt-6 text-sm text-gray-500 hover:text-blue-600 transition-colors">
                        العودة إلى الصفحة الرئيسية
                    </button>
                </div>
            </div>

        </div>
    );
};

const Dashboard = ({ allProps }) => {
    const { auth, schoolSettings, onUpdateParent, parents, ...dataAndHandlers } = allProps;
    const [page, setPage] = useState('home');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const { students, classes, enrollments, fees, teachers, subjects, timetable, users, showNotification, teacherPayments } = dataAndHandlers;

    // Banner that always shows unpaid/overdue fees regardless of academic year
    const UnpaidFeesBanner = ({ fees, students, onOpenFinance }) => {
        const unpaid = (fees || []).filter(f => f.status === 'unpaid' || f.status === 'overdue');
        if (!unpaid || unpaid.length === 0) return null;
        const total = unpaid.reduce((s, f) => s + (f.amount || 0), 0);
        const uniqueStudents = new Set(unpaid.map(f => f.studentId)).size;

        return (
            <div className="w-full bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 flex items-center justify-between gap-4">
                <div>
                    <p className="font-bold">يوجد {unpaid.length} أجور غير مدفوعة لـ {uniqueStudents} تلامذة</p>
                    <p className="text-sm text-yellow-700">إجمالي الأجور للخلاص: {total.toFixed(2)} د.ت — ستبقى هذه الأجور مرئية حتى يتم خلاصها.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={onOpenFinance} className="bg-yellow-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-yellow-700">اذهب للإدارة المالية</button>
                </div>
            </div>
        );
    };

    const t = (keyPath, fallback) => {
        const lang = schoolSettings?.language || 'ar';
        const parts = keyPath.split('.');
        let node = TRANSLATIONS[lang];
        for (const p of parts) {
            if (!node) break;
            node = node[p];
        }
        return node || fallback;
    };

    const pageTitles = t('pageTitles', TRANSLATIONS.ar.pageTitles);

    const ActiveLink = ({ children, pageName }) => (
        <button
            onClick={() => {
                setPage(pageName);
                setIsSidebarOpen(false); // Close sidebar on mobile after navigation
            }}
            className={`flex items-center space-x-3 rtl:space-x-reverse px-4 py-3 rounded-lg transition w-full text-right ${page === pageName ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
        >
            {children}
        </button>
    );

    const onChangeLanguage = async (lang) => {
        // update local settings immediately for responsiveness
        try {
            const newSettings = { ...(schoolSettings || {}), language: lang };
            if (dataAndHandlers && typeof dataAndHandlers.onUpdateSchoolSettings === 'function') {
                await dataAndHandlers.onUpdateSchoolSettings(newSettings);
            } else {
                // fallback: update local state only
                // Note: caller (App) listens to Firestore snapshot and will sync.
                // Here we optimistically set schoolSettings via a custom event if needed.
                // But we can also just set via a window-level event for local preview.
                window.__localSchoolSettings = newSettings;
            }
        } catch (err) {
            console.error('Failed to change language', err);
        }
    };

    const renderPage = () => {
        const stats = {
            students: students.length,
            classes: classes.length,
            teachers: teachers.length,
            unpaidFees: fees.filter(f => f.status === 'unpaid' || f.status === 'overdue').reduce((sum, f) => sum + f.amount, 0),
            overdueStudents: new Set(fees.filter(f => f.status === 'overdue' || f.status === 'unpaid').map(f => f.studentId)).size,
            newStudents: students.filter(s => s.registrationDate && (new Date() - s.registrationDate.toDate() < 7 * 24 * 60 * 60 * 1000)).length,
        };

        const openFinance = () => setPage('finance');

        switch (page) {
            case 'home':
                return (
                    <>
                        <UnpaidFeesBanner fees={fees} students={students} onOpenFinance={openFinance} />
                        <HomePage stats={stats} setPage={setPage} settings={schoolSettings} />
                    </>
                );
            case 'studentManagement':
                return (
                    <>
                        <UnpaidFeesBanner fees={fees} students={students} onOpenFinance={openFinance} />
                        <StudentManagementPage {...dataAndHandlers} parents={parents} />
                    </>
                );
            case 'parentManagement':
                return <ParentManagementPage students={students} showNotification={showNotification} t={t} onUpdateParent={onUpdateParent} />;
            case 'teacherManagement':
                return <TeacherManagementPage {...dataAndHandlers} />;
            case 'classManagement':
                return <ClassManagementPage schoolSettings={schoolSettings} {...dataAndHandlers} />;
            case 'subjectManagement':
                return <SubjectManagementPage {...dataAndHandlers} />;
            case 'timetable':
                return <TimetableManagementPage {...dataAndHandlers} />;
            case 'settings':
                return <SettingsPage schoolSettings={schoolSettings} {...dataAndHandlers} onUpdateSchoolSettings={dataAndHandlers.onUpdateSchoolSettings} />;
            case 'finance':
                return (
                    <>
                        <UnpaidFeesBanner fees={fees} students={students} onOpenFinance={openFinance} />
                        <FinancialManagement {...dataAndHandlers} db={db} appId={APP_ID} />
                    </>
                );
            case 'homeworkManagement':
                // Render HomeworkManagement directly, or AdminDashboard if you want to aggregate admin features
                return <HomeworkManagement classes={classes} />;
            case 'notifications':
                return <NotificationManagement classes={classes} students={students} enrollments={enrollments} />;
            default:
                return <HomePage stats={stats} setPage={setPage} settings={schoolSettings} />;
        }
    };

    const dir = isRTL(schoolSettings?.language) ? 'rtl' : 'ltr';
    const sidebarSideClass = isRTL(schoolSettings?.language) ? 'right-0' : 'left-0';
    const sidebarTranslateHide = isRTL(schoolSettings?.language) ? 'translate-x-full' : '-translate-x-full';

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    return (
        <div className="min-h-screen bg-gray-100 md:flex" dir={dir}>
            {/* نافذة تأكيد تسجيل الخروج */}
            {showLogoutModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-sm text-center mx-4">
                        <h2 className="text-lg font-bold mb-4">{t('labels.confirmLogout', 'هل أنت متأكد أنك تريد تسجيل الخروج؟')}</h2>
                        <div className="flex justify-center gap-4 mt-6">
                            <button
                                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                                onClick={() => setShowLogoutModal(false)}
                            >
                                إلغاء
                            </button>
                            <button
                                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                                onClick={() => {
                                    setShowLogoutModal(false);
                                    signOut(auth);
                                    sessionStorage.removeItem('schoolAppUser');
                                }}
                            >
                                تأكيد
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {isSidebarOpen && <div onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"></div>}

            <aside className={`fixed inset-y-0 ${sidebarSideClass} z-50 w-64 bg-white shadow-lg p-4 flex flex-col transform transition-transform duration-300 ease-in-out md:relative md:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : sidebarTranslateHide}`}>
                <div className="text-center py-4 border-b mb-4">
                    <img src={schoolSettings?.schoolLogo} alt="logo" className="mx-auto w-16 h-16 rounded-full object-cover mb-2" />
                    <h1 className="text-lg font-bold text-blue-600">{schoolSettings?.schoolName || (isRTL(schoolSettings?.language) ? 'مدرستي' : 'My School')}</h1>
                </div>
                <nav className="flex-grow space-y-2">
                    <ActiveLink pageName="home"><HomeIcon className="w-6 h-6" /><span>{t('pageTitles.home', TRANSLATIONS.ar.pageTitles.home)}</span></ActiveLink>
                    <ActiveLink pageName="studentManagement"><UsersGroupIcon className="w-6 h-6" /><span>{t('pageTitles.studentManagement', TRANSLATIONS.ar.pageTitles.studentManagement)}</span></ActiveLink>
                    <ActiveLink pageName="parentManagement"><ShieldCheckIcon className="w-6 h-6" /><span>{t('pageTitles.parentManagement', TRANSLATIONS.ar.pageTitles.parentManagement)}</span></ActiveLink>
                    <ActiveLink pageName="teacherManagement"><AcademicCapIcon className="w-6 h-6" /><span>{t('pageTitles.teacherManagement', TRANSLATIONS.ar.pageTitles.teacherManagement)}</span></ActiveLink>
                    <ActiveLink pageName="classManagement"><LibraryIcon className="w-6 h-6" /><span>{t('pageTitles.classManagement', TRANSLATIONS.ar.pageTitles.classManagement)}</span></ActiveLink>
                    <ActiveLink pageName="subjectManagement"><BookOpenIcon className="w-6 h-6" /><span>{t('pageTitles.subjectManagement', TRANSLATIONS.ar.pageTitles.subjectManagement)}</span></ActiveLink>
                    <ActiveLink pageName="timetable"><CalendarIcon className="w-6 h-6" /><span>{t('pageTitles.timetable', TRANSLATIONS.ar.pageTitles.timetable)}</span></ActiveLink>
                    <ActiveLink pageName="finance"><DollarSignIcon className="w-6 h-6" /><span>{t('pageTitles.finance', TRANSLATIONS.ar.pageTitles.finance)}</span></ActiveLink>
                    <ActiveLink pageName="homeworkManagement"><ClipboardDocumentCheckIcon className="w-6 h-6" /><span>{t('pageTitles.homeworkManagement', TRANSLATIONS.ar.pageTitles.homeworkManagement)}</span></ActiveLink>
                    <ActiveLink pageName="notifications"><BellIcon className="w-6 h-6" /><span>إدارة الإشعارات</span></ActiveLink>
                </nav>
                <div className="mt-auto pt-4 border-t">
                    <div className="flex flex-col space-y-2">
                        <ActiveLink pageName="settings"><Cog6ToothIcon className="w-6 h-6" /><span>{t('pageTitles.settings', TRANSLATIONS.ar.pageTitles.settings)}</span></ActiveLink>
                        <button onClick={() => setShowLogoutModal(true)} className="flex items-center space-x-3 rtl:space-x-reverse px-4 py-3 rounded-lg transition w-full text-right text-gray-600 hover:bg-gray-200">
                            <LogoutIcon className="w-6 h-6" />
                            <span>{t('labels.logout', 'Logout')}</span>
                        </button>
                    </div>

                    {/* Mobile navigation bar at the bottom */}
                    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 pb-safe md:hidden z-50">
                        <div className="flex items-center justify-between px-4">
                            <button onClick={() => setShowLogoutModal(true)} className="flex-1 flex flex-col items-center justify-center py-2">
                                <LogoutIcon className="w-6 h-6 text-red-600" />
                                <span className="text-xs">تسجيل الخروج</span>
                            </button>
                            <button onClick={() => { setIsSidebarOpen(false); setPage('settings'); }} className="flex-1 flex flex-col items-center justify-center py-2">
                                <Cog6ToothIcon className="w-6 h-6 text-gray-600" />
                                <span className="text-xs">الإعدادات</span>
                            </button>
                            <button onClick={() => { setIsSidebarOpen(false); setPage('finance'); }} className="flex-1 flex flex-col items-center justify-center py-2">
                                <DollarSignIcon className="w-6 h-6 text-yellow-600" />
                                <span className="text-xs">مالية</span>
                            </button>
                        </div>
                    </div>
                </div>
            </aside>

            <div className="flex-1 flex flex-col">
                <header className="bg-white shadow-sm p-3 flex justify-between items-center sticky top-0 z-30 header-gradient">
                    <div className="flex items-center gap-3">
                        {/* Mobile hamburger + small logo + school name */}
                        <button onClick={() => setIsSidebarOpen(true)} className="text-gray-600 md:hidden" aria-label="فتح القائمة">
                            <Bars3Icon className="w-6 h-6" />
                        </button>
                        <img src={schoolSettings?.schoolLogo} alt="شعار" className="w-8 h-8 rounded-full object-cover md:hidden" />
                        <div className="flex flex-col text-right md:hidden">
                            <span className="text-sm font-semibold text-gray-800">{schoolSettings?.schoolName}</span>
                            <span className="text-xs text-gray-500">{t(`pageTitles.${page}`, TRANSLATIONS.ar.pageTitles[page])}</span>
                        </div>

                        {/* Desktop title */}
                        <h2 className="hidden md:block text-xl font-bold text-gray-800">{t(`pageTitles.${page}`, TRANSLATIONS.ar.pageTitles[page])}</h2>
                    </div>

                    <div className="flex items-center space-x-4 rtl:space-x-reverse">
                        <button className="relative text-gray-600 hover:text-blue-600">
                            <BellIcon className="w-7 h-7" />
                            <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                            </span>
                        </button>

                        {/* Language selector - flags */}
                        <div className="flex items-center gap-2">
                            {Object.keys(LANGUAGE_FLAGS).map(l => (
                                <button key={l} onClick={() => onChangeLanguage(l)} title={l} className={`text-sm p-1 rounded-md hover:bg-gray-100 ${schoolSettings?.language === l ? 'ring-2 ring-blue-300' : ''}`}>
                                    <span style={{ fontSize: '18px' }}>{LANGUAGE_FLAGS[l]}</span>
                                </button>
                            ))}
                        </div>

                        <div className="text-sm text-gray-500 hidden md:block">{new Date().toLocaleDateString(APP_LOCALE, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                    </div>
                </header>
                <main className="flex-1">
                    {renderPage()}
                </main>
            </div>
        </div>
    );
};


// --- Main Application Component ---
export default function App() {
    // Using the global appId variable
    const appId = typeof __app_id !== 'undefined' ? __app_id : 'school-management-system-app';
    const [students, setStudents] = useState([]);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [timetable, setTimetable] = useState([]);
    const [enrollments, setEnrollments] = useState([]);
    const [fees, setFees] = useState([]);
    const [users, setUsers] = useState([]);
    const [notification, setNotification] = useState({ message: '', type: '' });
    const [user, setUser] = useState(() => {
        const sessionUser = sessionStorage.getItem('schoolAppUser');
        return sessionUser ? JSON.parse(sessionUser) : null;
    });
    const [isLoading, setIsLoading] = useState(true);
    const [view, setView] = useState('landing'); // 'landing' or 'login'
    const [teacherPayments, setTeacherPayments] = useState([]);

    // توحيد تعريف `appId` داخل المكون الرئيسي
    // أنواع صفحات العرض المتاحة
    const VIEW_TYPES = {
        LANDING: 'landing',
        ADMIN_LOGIN: 'admin_login',
        PARENT_LOGIN: 'parent_login',
        DASHBOARD: 'dashboard',
        PARENT_PORTAL: 'parent_portal'
    };

    const [schoolSettings, setSchoolSettings] = useState({
        schoolName: 'Pythagore School',
        schoolLogo: `https://pythagoreschool.com/logo192.png`,
        language: 'ar',
        timezone: 'Africa/Tunis',
        academicYearStart: '2024-09-15',
        academicYearEnd: '2025-06-30'
    });

    const parents = useMemo(() => {
        const parentMap = new Map();
        students.forEach(student => {
            if (student.parentPhone) {
                if (!parentMap.has(student.parentPhone)) {
                    parentMap.set(student.parentPhone, {
                        id: student.parentPhone, // Using phone as ID for existing parents
                        name: student.fatherName || student.motherName || 'غير متوفر',
                        phone: student.parentPhone,
                        email: student.email || '',
                    });
                }
                // Optionally, update parent info if a student has more complete details
                const parent = parentMap.get(student.parentPhone);
                if (parent.name === 'غير متوفر' && (student.fatherName || student.motherName)) {
                    parent.name = student.fatherName || student.motherName;
                }
                if (!parent.email && student.email) {
                    parent.email = student.email;
                }
            }
        });
        return Array.from(parentMap.values());
    }, [students]); // Re-calculate when students data changes

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => { // Added 'async'
            if (firebaseUser) {
                // Fetch user role from Firestore
                const userDocRef = doc(db, `artifacts/${appId}/public/data/users`, firebaseUser.uid);
                const userDocSnap = await getDoc(userDocRef); // Added 'await'

                let userRole = 'غير_معرف'; // Default role
                if (userDocSnap.exists()) {
                    userRole = userDocSnap.data().role;
                }

                const userData = {
                    uid: firebaseUser.uid,
                    email: firebaseUser.email,
                    displayName: firebaseUser.displayName || '',
                    role: userRole, // Include the fetched role
                };
                sessionStorage.setItem('schoolAppUser', JSON.stringify(userData));
                setUser(userData);
            } else {
                // حذف بيانات الجلسة عند تسجيل الخروج
                sessionStorage.removeItem('schoolAppUser');
                setUser(null);
                setView('landing'); // Go back to landing page on logout
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, [appId]);

    // keep APP_LOCALE in sync with settings.language
    useEffect(() => {
        try {
            const lang = schoolSettings?.language || 'ar';
            APP_LOCALE = LOCALE_MAP[lang] || LOCALE_MAP['ar'];
            // expose minimally for t() fallback in headers defined above
            if (typeof window !== 'undefined') {
                window.__school_settings = window.__school_settings || {};
                window.__school_settings.language = lang;
            }
        } catch (err) {
            // ignore
        }
    }, [schoolSettings?.language]);

    // Function to generate monthly fees
    const generateMonthlyFees = async () => {
        if (!db || !schoolSettings?.feesPerLevel) return;

        const now = new Date();
        const currentDay = now.getDate();
        const feeGenerationDay = schoolSettings.feeGenerationDay || 1;

        // Only generate fees on the configured day of the month
        if (currentDay !== feeGenerationDay) return;

        // Get all students with their class information
        const activeEnrollments = enrollments.map(enrollment => {
            const student = students.find(s => s.id === enrollment.studentId);
            const studentClass = classes.find(c => c.id === enrollment.classId);
            return { student, studentClass, enrollment };
        }).filter(item => item.student && item.studentClass);

        // Generate fees for each enrolled student
        for (const { student, studentClass } of activeEnrollments) {
            // Get the fee amount for this student's level
            const feeAmount = schoolSettings.feesPerLevel[studentClass.level];
            if (!feeAmount) continue;

            const monthName = new Intl.DateTimeFormat('ar-TN', { month: 'long' }).format(now);
            const description = `رسوم شهر ${monthName}`;

            // Check if fee for this month already exists
            const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
            const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

            const existingFees = fees.filter(f =>
                f.studentId === student.id &&
                f.description.includes(monthName) &&
                new Date(f.createdAt?.seconds * 1000) >= monthStart &&
                new Date(f.createdAt?.seconds * 1000) <= monthEnd
            );

            // Only create fee if it doesn't exist
            if (existingFees.length === 0) {
                const nextDueDate = new Date(now.getFullYear(), now.getMonth(), feeGenerationDay);
                // If we're generating fees after the due date, set due date to next month
                if (now.getDate() > feeGenerationDay) {
                    nextDueDate.setMonth(nextDueDate.getMonth() + 1);
                }

                await addDoc(collection(db, `artifacts/${appId}/public/data/fees`), {
                    studentId: student.id,
                    amount: feeAmount,
                    description,
                    dueDate: nextDueDate.toISOString(),
                    status: 'unpaid',
                    createdAt: serverTimestamp()
                });
            }
        }
    };

    // Effect for data synchronization
    useEffect(() => {
        if (!db || !user) return;

        const collections = ['students', 'classes', 'teachers', 'subjects', 'timetable', 'enrollments', 'fees', 'users', 'settings', 'teacherPayments'];
        const setters = {
            students: setStudents, classes: setClasses, teachers: setTeachers, subjects: setSubjects,
            timetable: setTimetable, enrollments: setEnrollments, fees: setFees, users: setUsers, teacherPayments: setTeacherPayments
        };

        console.log('Starting data synchronization...'); // للتأكد من تشغيل المزامنة

        const unsubs = [];
        collections.forEach(colName => {
            try {
                // تأكد من وجود db وappId
                if (!db) throw new Error('Firestore `db` is not initialized');

                // استخدام المسار الكامل للمجموعة
                const collPath = `artifacts/${appId}/public/data/${colName}`;
                console.log(`Subscribing to collection: ${collPath}`); // تتبع المجموعات التي نحاول الاتصال بها

                const collRef = collection(db, collPath);
                const unsub = onSnapshot(collRef,
                    (snapshot) => {
                        console.log(`Received ${snapshot.docs.length} documents from ${colName}`); // تتبع عدد المستندات المستلمة
                        const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                        if (colName === 'settings') {
                            if (data.length > 0 && data[0].school) {
                                setSchoolSettings(data[0].school);
                            }
                        } else if (setters[colName]) {
                            setters[colName](data);
                        }
                    },
                    (error) => {
                        console.error(`Error fetching ${colName}:`, error);
                        showNotification(`خطأ في جلب بيانات ${colName}`, 'error');
                    }
                );
                unsubs.push(unsub);
            } catch (err) {
                console.error(`Failed to subscribe to collection ${colName}:`, err);
                showNotification(`فشل الاتصال بقاعدة البيانات: ${err.message}`, 'error');
            }
        });

        return () => unsubs.forEach(unsub => unsub());
    }, [user, appId]);

    // Keep module-level APP_LOCALE in sync with settings.language
    useEffect(() => {
        try {
            const lang = schoolSettings?.language || 'ar';
            APP_LOCALE = LOCALE_MAP[lang] || LOCALE_MAP['ar'];
        } catch (err) {
            APP_LOCALE = LOCALE_MAP['ar'];
        }
    }, [schoolSettings?.language]);


    const showNotification = (message, type = 'info') => {
        setNotification({ message, type });
    };

    const onAddSubject = async (subjectData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/subjects`), { name: subjectData.name });
        showNotification('تمت إضافة المادة بنجاح!', 'success');
    };
    const onUpdateSubject = async (subjectData) => {
        if (!db) return;
        const subjectRef = doc(db, `artifacts/${appId}/public/data/subjects`, subjectData.id);
        await setDoc(subjectRef, { name: subjectData.name }, { merge: true });
        showNotification('تم تحديث المادة بنجاح!', 'success');
    };
    const onDeleteSubject = async (subjectId) => {
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/subjects`, subjectId));
        showNotification('تم حذف المادة بنجاح.', 'success');
    };

    // Helper to generate a random password
    const generateRandomPassword = () => {
        return Math.random().toString(36).slice(-8); // Simple 8-char alphanumeric
    };

    const onAddStudent = async (studentData) => {
        if (!db || !auth) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }

        try {
            const studentsQuery = query(collection(db, `artifacts/${appId}/public/data/students`));
            const querySnapshot = await getDocs(studentsQuery);
            const studentCount = querySnapshot.size;

            // تعديل الرقم الدراسي ليكون من السنة و6 أرقام
            const currentYear = new Date().getFullYear();
            // إنشاء رقم تسلسلي من 6 أرقام مع أصفار في البداية
            const sequentialNumber = String(studentCount + 1).padStart(6, '0');
            const newStudentId = studentData.studentId || `${currentYear}${sequentialNumber}`;

            const { id, email, parentPhone, ...data } = studentData;
            const photoUrl = data.photoUrl || `https://placehold.co/100x100/E2E8F0/4A5568?text=${data.firstName}`;

            const newStudentForFirestore = {
                ...data,
                studentId: newStudentId,
                email,
                parentPhone,
                photoUrl,
                registrationDate: serverTimestamp(),
            };

            const studentDocRef = await addDoc(collection(db, `artifacts/${appId}/public/data/students`), newStudentForFirestore);

            const newStudentForState = {
                ...newStudentForFirestore,
                id: studentDocRef.id,
                registrationDate: { toDate: () => new Date() } // Mimic Timestamp for local state
            };

            // Manually update the local state to reflect the change immediately
            setStudents(prevStudents => [...prevStudents, newStudentForState]);

            showNotification(`تمت إضافة الطالب ${studentData.firstName} بنجاح! رقمه الدراسي هو ${newStudentId}`, 'success');

        } catch (error) {
            console.error("Error adding student:", error);
            showNotification('حدث خطأ أثناء إضافة الطالب.', 'error');
        }
    };
    const onUpdateStudent = async (studentData) => {
        if (!db) return;
        const studentRef = doc(db, `artifacts/${appId}/public/data/students`, studentData.id);
        await setDoc(studentRef, studentData, { merge: true });
        showNotification('تم تحديث بيانات التلميذ بنجاح!', 'success');
    };
    const onUpdateParent = async (oldParentPhone, parentData, selectedChildren) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }

        const batch = writeBatch(db);
        const newParentPhone = parentData.phone;

        try {
            // 1. Get all students currently associated with the old parent phone
            const currentStudentsSnapshot = await getDocs(query(
                collection(db, `artifacts/${appId}/public/data/students`),
                where('parentPhone', '==', oldParentPhone)
            ));
            const currentStudentIds = new Set(currentStudentsSnapshot.docs.map(doc => doc.id));

            // 2. Process students who were previously linked to this parent
            for (const docSnapshot of currentStudentsSnapshot.docs) {
                const studentId = docSnapshot.id;
                const studentRef = doc(db, `artifacts/${appId}/public/data/students`, studentId);

                if (selectedChildren.includes(studentId)) {
                    // Student remains associated: update parent details and phone if changed
                    batch.update(studentRef, {
                        fatherName: parentData.name, // Assuming parent.name maps to fatherName
                        email: parentData.email,
                        parentPhone: newParentPhone,
                    });
                } else {
                    // Student is no longer associated: nullify parentPhone
                    batch.update(studentRef, { parentPhone: null });
                }
            }

            // 3. Process students who are newly associated with this parent
            // These are students in `selectedChildren` that were NOT previously linked to `oldParentPhone`
            const allStudentsSnapshot = await getDocs(collection(db, `artifacts/${appId}/public/data/students`));
            for (const docSnapshot of allStudentsSnapshot.docs) {
                const studentId = docSnapshot.id;
                const studentRef = doc(db, `artifacts/${appId}/public/data/students`, studentId);
                const studentData = docSnapshot.data();

                if (selectedChildren.includes(studentId) && studentData.parentPhone !== newParentPhone) {
                    // Student is in selectedChildren and not already linked to this new phone
                    batch.update(studentRef, { parentPhone: newParentPhone });
                }
            }

            // 4. Update the user record in the 'users' collection if it exists
            // Assuming parent user accounts are identified by email
            const userQuery = query(collection(db, `artifacts/${appId}/public/data/users`), where('email', '==', parentData.email));
            const userSnapshot = await getDocs(userQuery);

            if (!userSnapshot.empty) {
                userSnapshot.forEach(userDoc => {
                    const userRef = doc(db, `artifacts/${appId}/public/data/users`, userDoc.id);
                    batch.update(userRef, {
                        name: parentData.name,
                        // If phone is stored in user record and needs to be updated:
                        // phone: newParentPhone,
                    });
                });
            }

            await batch.commit();
            showNotification('تم تحديث بيانات ولي الأمر بنجاح!', 'success');

        } catch (error) {
            console.error('Error updating parent:', error);
            showNotification('حدث خطأ أثناء تحديث بيانات ولي الأمر.', 'error');
        }
    };
    const onDeleteStudent = async (studentId) => {
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/students`, studentId));
        showNotification('تم حذف التلميذ وبياناته المرتبطة.', 'success');
    };
    const onAddTeacher = async (teacherData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        const { id, ...data } = teacherData;
        const photoUrl = data.photoUrl || `https://placehold.co/100x100/E2E8F0/4A5568?text=${data.name.split(' ')[1]}`;
        await addDoc(collection(db, `artifacts/${appId}/public/data/teachers`), { ...data, photoUrl, joinDate: serverTimestamp() });
        showNotification('تمت إضافة المعلم بنجاح!', 'success');
    };
    const onUpdateTeacher = async (teacherData) => {
        if (!db) return;
        const teacherRef = doc(db, `artifacts/${appId}/public/data/teachers`, teacherData.id);
        await setDoc(teacherRef, teacherData, { merge: true });
        showNotification('تم تحديث بيانات المعلم بنجاح!', 'success');
    };
    const onDeleteTeacher = async (teacherId) => {
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/teachers`, teacherId));
        showNotification('تم حذف المعلم.', 'success');
    };
    const onAddClass = async (classData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/classes`), classData);
        showNotification('تمت إضافة الفصل بنجاح!', 'success');
    };
    const onUpdateClass = async (classData) => {
        if (!db) return;
        const classRef = doc(db, `artifacts/${appId}/public/data/classes`, classData.id);
        await setDoc(classRef, classData, { merge: true });
        showNotification('تم تحديث بيانات الفصل بنجاح!', 'success');
    };
    const onDeleteClass = async (classId) => {
        if (enrollments.some(e => e.classId === classId)) {
            showNotification('لا يمكن حذف الفصل، لا يزال به تلامذة مسجلون.', 'error');
            return;
        }
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/classes`, classId));
        showNotification('تم حذف الفصل بنجاح.', 'success');
    };
    const onAddFee = async (feeData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/fees`), { ...feeData, createdAt: serverTimestamp() });
        showNotification('تمت إضافة الرسم المالي بنجاح!', 'success');
    };
    const onUpdateFeeStatus = async (feeId, status) => {
        if (!db) return;
        const feeRef = doc(db, `artifacts/${appId}/public/data/fees`, feeId);
        await setDoc(feeRef, { status }, { merge: true });
        showNotification('تم تحديث حالة الدفع.', 'success');
    };

    const onDeleteFee = async (feeId) => {
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/fees`, feeId));
        showNotification('تم حذف الرسم المالي.', 'success');
    };

    const onPayTeacher = async (paymentData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/teacherPayments`), { ...paymentData, date: serverTimestamp() });
        showNotification('تم دفع راتب المعلم بنجاح', 'success');
    };

    const onDeleteTeacherPayment = async (paymentId) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/teacherPayments`, paymentId));
        showNotification('تم حذف دفعة المعلم بنجاح', 'success');
    };

    // Check for automatic fee generation daily
    useEffect(() => {
        if (db && schoolSettings?.feesPerLevel) {
            const checkInterval = setInterval(generateMonthlyFees, 24 * 60 * 60 * 1000); // Check daily
            generateMonthlyFees(); // Check immediately on load
            return () => clearInterval(checkInterval);
        }
    }, [db, schoolSettings?.feesPerLevel, students, enrollments, classes, fees]);
    const onAddTimetableEntry = async (sessionData) => {
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/timetable`), sessionData);
        showNotification('تمت إضافة الحصة بنجاح', 'success');
    };
    const onUpdateTimetableEntry = async (sessionData) => {
        if (!db) return;
        const sessionRef = doc(db, `artifacts/${appId}/public/data/timetable`, sessionData.id);
        await setDoc(sessionRef, sessionData, { merge: true });
        showNotification('تم تحديث الحصة بنجاح', 'success');
    };
    const onDeleteTimetableEntry = async (sessionId) => {
        if (!db) return;
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/timetable`, sessionId));
        showNotification('تم حذف الحصة بنجاح', 'success');
    };
    const onUpdateSchoolSettings = async (newSettings) => {
        if (!db) return;

        const cleanSettings = {
            schoolName: newSettings.schoolName || '',
            schoolLogo: newSettings.schoolLogo || '',
            language: newSettings.language || 'ar',
            timezone: newSettings.timezone || 'Africa/Tunis',
            academicYearStart: newSettings.academicYearStart || '',
            academicYearEnd: newSettings.academicYearEnd || '',
            feeGenerationDay: newSettings.feeGenerationDay || 1,
            feesPerLevel: newSettings.feesPerLevel || {},
            levelOrder: newSettings.levelOrder || [],
        };

        // Handle logo upload if it's a new base64 image
        if (cleanSettings.schoolLogo && cleanSettings.schoolLogo.startsWith('data:image')) {
            try {
                // تحويل صورة Base64 إلى ملف عادي
                // استخراج نوع الملف من الـ Base64
                const mimeType = cleanSettings.schoolLogo.split(';')[0].split(':')[1];
                const fileExtension = mimeType === 'image/png' ? '.png' : '.jpg';

                // Convert data URL to blob
                const response = await fetch(cleanSettings.schoolLogo);
                const blob = await response.blob();

                const formData = new FormData();
                formData.append('logo', blob, `logo${fileExtension}`);

                const uploadResponse = await fetch('http://localhost:3001/upload', {
                    method: 'POST',
                    body: formData,
                });

                if (!uploadResponse.ok) {
                    throw new Error('Server upload failed');
                }

                const uploadResult = await uploadResponse.json();
                cleanSettings.schoolLogo = uploadResult.filePath;

                console.log("تم تحويل الصورة من Base64 إلى ملف عادي بنجاح:", cleanSettings.schoolLogo);

            } catch (error) {
                console.error("خطأ في تحميل الشعار الجديد إلى السيرفر:", error);
                showNotification('حدث خطأ أثناء تحميل الشعار الجديد.', 'error');
                return; // Don't proceed if logo upload fails
            }
        }

        // Validate fee settings
        if (cleanSettings.feesPerLevel) {
            for (const [level, amount] of Object.entries(cleanSettings.feesPerLevel)) {
                if (amount < 0) {
                    showNotification('لا يمكن أن تكون الرسوم بقيمة سالبة', 'error');
                    return;
                }
            }
        }
        const settingsRef = doc(db, `artifacts/${appId}/public/data/settings`, 'main');
        await setDoc(settingsRef, { school: JSON.parse(JSON.stringify(cleanSettings)) }, { merge: true });
        showNotification('تم تحديث إعدادات النظام بنجاح!', 'success');
    };
    const onAddUser = async (userData) => {
        if (!auth || !db) {
            showNotification('النظام غير جاهز. يرجى المحاولة لاحقاً.', 'error');
            return;
        }

        try {
            // For parent accounts, validate student association
            if (userData.role === 'ولي_أمر') {
                if (!userData.studentId) {
                    showNotification('يجب ربط حساب ولي الأمر بطالب.', 'error');
                    return;
                }
                // Verify student exists
                const student = students.find(s => s.id === userData.studentId);
                if (!student) {
                    showNotification('الطالب المحدد غير موجود.', 'error');
                    return;
                }
                // Check if student already has a parent account
                const existingParent = users.find(u => u.role === 'ولي_أمر' && u.studentId === userData.studentId);
                if (existingParent) {
                    showNotification('يوجد بالفعل حساب ولي أمر مرتبط بهذا الطالب.', 'error');
                    return;
                }
            }

            // Create Firebase Auth account
            const userCredential = await createUserWithEmailAndPassword(auth, userData.email, userData.password);
            const newUserId = userCredential.user.uid;

            // Store additional user data in Firestore
            const { password, ...firestoreData } = userData;
            const userDoc = {
                ...firestoreData,
                createdAt: serverTimestamp(),
                lastLogin: null,
                status: 'active'
            };

            // Special handling for parent accounts
            if (userData.role === 'ولي_أمر') {
                const student = students.find(s => s.id === userData.studentId);
                userDoc.studentName = `${student.firstName} ${student.lastName}`;
            }

            await setDoc(doc(db, `artifacts/${appId}/public/data/users`, newUserId), userDoc);

            // Show appropriate success message
            const successMsg = userData.role === 'ولي_أمر'
                ? 'تم إنشاء حساب ولي الأمر بنجاح وربطه بالطالب.'
                : 'تمت إضافة المستخدم بنجاح!';
            showNotification(successMsg, 'success');

        } catch (error) {
            console.error("Error adding user:", error);
            let errorMsg = 'حدث خطأ أثناء إضافة المستخدم.';

            if (error.code === 'auth/email-already-in-use') {
                errorMsg = 'هذا البريد الإلكتروني مستخدم بالفعل.';
            } else if (error.code === 'auth/invalid-email') {
                errorMsg = 'البريد الإلكتروني غير صالح.';
            } else if (error.code === 'auth/weak-password') {
                errorMsg = 'كلمة المرور ضعيفة جداً. يجب أن تحتوي على 6 أحرف على الأقل.';
            }

            showNotification(errorMsg, 'error');
        }
    };

    const onUpdateUser = async (userData) => {
        if (!db) return;
        const { id, ...data } = userData;
        const userRef = doc(db, `artifacts/${appId}/public/data/users`, id);
        await setDoc(userRef, data, { merge: true });
        showNotification('تم تحديث بيانات المستخدم بنجاح!', 'success');
    };

    const onDeleteUser = async (userId) => {
        if (!db) return;
        // Note: Deleting a user from Firebase Auth requires Admin SDK. 
        // This only deletes the user record from Firestore.
        await deleteDoc(doc(db, `artifacts/${appId}/public/data/users`, userId));
        showNotification('تم حذف المستخدم من قاعدة البيانات.', 'success');
    };
    const onAddEnrollment = async (enrollmentData) => {
        if (enrollments.some(e => e.studentId === enrollmentData.studentId)) {
            showNotification('هذا التلميذ مسجل بالفعل في قسم.', 'error');
            return;
        }
        if (!db) { showNotification('قاعدة البيانات غير جاهزة. حاول لاحقًا.', 'error'); return; }
        await addDoc(collection(db, `artifacts/${appId}/public/data/enrollments`), { ...enrollmentData, enrollmentDate: serverTimestamp() });
        showNotification('تم تسجيل التلميذ في القسم بنجاح!', 'success');
    };
    const onDeleteEnrollment = async (studentId, classId) => {
        if (!db) return;
        const enrollment = enrollments.find(e => e.studentId === studentId && e.classId === classId);
        if (enrollment) {
            await deleteDoc(doc(db, `artifacts/${appId}/public/data/enrollments`, enrollment.id));
            showNotification('تم إلغاء تسجيل التلميذ من الفصل.', 'success');
        }
    };
    const onUpdateEnrollment = async (enrollmentId, newClassId) => {
        if (!db) return;
        const enrollmentRef = doc(db, `artifacts/${appId}/public/data/enrollments`, enrollmentId);
        await setDoc(enrollmentRef, { classId: newClassId }, { merge: true });
        showNotification('تم تحديث صف التلميذ بنجاح!', 'success');
    };

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-100 flex justify-center items-center">
                <Spinner /> <span className="ml-4 text-lg">جاري التحميل...</span>
            </div>
        )
    }

    const allProps = {
        auth,
        schoolSettings,
        students,
        classes,
        teachers,
        subjects,
        timetable,
        enrollments,
        fees,
        users,
        teacherPayments,
        showNotification,
        onAddSubject,
        onUpdateSubject,
        onDeleteSubject,
        onDeleteTeacherPayment,
        db,
        appId: __app_id,
        onAddStudent,
        onUpdateStudent,
        onDeleteStudent,
        onAddTeacher,
        onUpdateTeacher,
        onDeleteTeacher,
        onAddClass,
        onUpdateClass,
        onDeleteClass,
        onAddFee,
        onUpdateFeeStatus,
        onDeleteFee,
        onPayTeacher,
        onAddTimetableEntry,
        onUpdateTimetableEntry,
        onDeleteTimetableEntry,
        onUpdateSchoolSettings,
        onUpdateUser,
        onAddUser,
        onDeleteUser,
        onAddEnrollment,
        onDeleteEnrollment,
        onUpdateEnrollment,
        onUpdateParent,
        parents,
    };

    const getCurrentView = () => {
        // إذا كان المستخدم مسجل الدخول
        if (user) {
            // تحقق من دور المستخدم
            if (user.role === 'ولي_أمر') {
                return (
                    <ParentPortal
                        schoolSettings={schoolSettings}
                        onBackToLanding={() => {
                            signOut(auth); // Sign out Firebase user
                            sessionStorage.removeItem('schoolAppUser'); // Clear session
                            setUser(null); // Clear user state
                            setView(VIEW_TYPES.LANDING); // Go to landing page
                        }}
                    // Pass necessary props to ParentPortal if it needs them
                    // For example, if ParentPortal needs student data linked to this parent
                    // you might need to fetch it here or pass it from `allProps` if available
                    // For now, assuming ParentPortal handles its own data fetching based on auth user
                    />
                );
            } else {
                // للمدير، المعلم، الإداري، إلخ.
                return <Dashboard allProps={allProps} />;
            }
        }

        // إذا لم يكن مسجل الدخول، اعرض الصفحة المناسبة حسب نوع العرض
        switch (view) {
            case VIEW_TYPES.LANDING:
                return (
                    <LandingPage
                        schoolSettings={schoolSettings}
                        onNavigateToLogin={() => setView(VIEW_TYPES.ADMIN_LOGIN)}
                        onNavigateToParentLogin={() => setView(VIEW_TYPES.PARENT_LOGIN)}
                    />
                );
            case VIEW_TYPES.ADMIN_LOGIN:
                return (
                    <LoginPage
                        auth={auth}
                        showNotification={showNotification}
                        schoolSettings={schoolSettings}
                        onBackToLanding={() => setView(VIEW_TYPES.LANDING)}
                    />
                );
            case VIEW_TYPES.PARENT_LOGIN:
                // This case is for when a user explicitly navigates to the parent login page
                // before authentication. After authentication, the `if (user)` block above
                // will handle the redirection based on role. If a parent logs in directly from here,
                // the onAuthStateChanged will fetch their role and redirect them to ParentPortal.
                return (
                    <ParentPortal
                        schoolSettings={schoolSettings}
                        db={db}
                        appId={appId}
                        onBackToLanding={() => setView(VIEW_TYPES.LANDING)}
                    />
                );
            default:
                return <div>خطأ: صفحة غير موجودة</div>;
        }
    };

    return (
        <>
            <GlobalStyles />
            <Notification
                message={notification.message}
                type={notification.type}
                onClear={() => setNotification({ message: '', type: '' })}
            />
            {getCurrentView()}
        </>
    );
}